<template>
  <b-container fluid class="responsive-container">
    <PromoCodeDiscrepancyModal
      :show="showPromoCodeModal"
      :renewalPromoCode="renewalPromoCode"
      :formPromoCode="tempFormPromoCode"
      @use-renewal-promo="useRenewalPromoCode"
      @use-form-promo="useFormPromoCode"
    />
    <div>
      <b-modal
        ref="my-modal"
        hide-footer
        title="Membership Confirmation"
        :no-close-on-backdrop="true"
        :no-close-on-esc="true"
        :hide-header-close="true"
      >
        <div class="d-block text-center">
          <h3>
            Successfully processed
            {{ this.isRenew == true ? "Renewal" : "New" }}
            {{ this.CardSelection.includes("Trial") ? "Trial " : "" }}Membership
            for {{ this.account.name }}.
          </h3>
        </div>
        <b-alert
          v-if="this.CardSelection.includes('Trial')"
          show
          variant="info"
          class="mt-3 mb-0 text-center"
        >
          <strong>This is a trial - no payment was collected.</strong>
        </b-alert>
        <h5 class="mt-3 mb-2">Payment Summary</h5>
        <b-list-group class="mb-2">
          <b-list-group-item
            v-for="line in paymentSummaryLines"
            :key="line.label"
            class="d-flex justify-content-between align-items-center py-1"
          >
            <span class="text-muted">{{ line.label }}</span>
            <strong>{{ line.value }}</strong>
          </b-list-group-item>
        </b-list-group>
        <b-button
          class="mt-3"
          variant="outline-success"
          block
          @click="hideModal"
          >Click to Close</b-button
        >
      </b-modal>

      <b-modal
        ref="trial-eligibility-modal"
        hide-footer
        title="Trial Not Available"
        :no-close-on-backdrop="false"
        :no-close-on-esc="false"
        @hide="revertCardSelection"
      >
        <div class="d-block">
          <p>This membership is not eligible for a trial at this time.</p>
          <p v-if="nextTrialEligibleDate">
            The next time they're eligible for a trial is
            <strong>{{ formatDate(nextTrialEligibleDate) }}</strong
            >.
          </p>
        </div>
        <b-button
          class="mt-3"
          variant="primary"
          block
          @click="closeTrialEligibilityModal"
          >OK</b-button
        >
      </b-modal>

      <b-form @submit.prevent="submitSearchForm" id="dealer-form">
        <b-card bg-variant="light">
          <b-form-group
            label-cols-lg="3"
            label="Search for a membership"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          ></b-form-group>

          <b-row>
            <b-col cols="12" class="mb-3">
              <b-form-input
                id="membership_number__c"
                v-model="$v.membership_number__c.$model"
                placeholder="Name, email, phone, or membership number"
              ></b-form-input>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" md="3" class="mb-2">
              <b-button
                @click="toggleBusy"
                type="submit"
                variant="primary"
                size="sm"
                block
                :disabled="
                  this.membership_number__c == null ||
                  this.membership_number__c == ''
                "
                >Submit</b-button
              >
            </b-col>

            <b-col cols="12" md="3" class="mb-2">
              <b-button
                @click="clearForm"
                type="button"
                variant="outline-secondary"
                size="sm"
                block
                >Clear Form</b-button
              >
            </b-col>

            <b-col
              cols="12"
              md="3"
              class="mb-2"
              v-if="this.response_data.length > 0"
            >
              <b-button
                @click="clearTable"
                type="submit"
                variant="primary"
                size="sm"
                block
                :disabled="!this.response_data.length > 0"
                >Clear Search</b-button
              >
            </b-col>

            <b-col cols="12" md="3" class="mb-2">
              <b-button
                @click="toggleCamera"
                type="button"
                variant="outline-primary"
                size="sm"
                block
                >Scan QR Code
              </b-button>
            </b-col>
          </b-row>

          <template>
            <div style="padding-top: 10px">
              <b-table
                v-if="response_data.length > 0"
                class="text-nowrap"
                :fields="tableFields"
                :items="visibleSearchResults"
                :busy="isBusy"
                bordered
                striped
                responsive
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Searching...</strong>
                  </div>
                </template>
                <template #cell(show_details)="row">
                  <b-button
                    size="sm"
                    @click="RenewMembership(row, row.index, row.detailsShowing)"
                    class="mr-2"
                  >
                    {{ row.detailsShowing ? "Clear From Form" : "Fill Form" }}
                  </b-button>
                </template>

                <template #head(send_pass)="">
                  <div class="d-flex align-items-center justify-content-center">
                    Send Digital Card
                    <b-icon
                      icon="info-circle-fill"
                      class="ml-2"
                      v-b-tooltip.hover
                      title="The Digital Card will be sent to the primary email on the contact in Salesforce"
                    />
                  </div>
                </template>

                <template #cell(send_pass)="row">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="onSendPassClick(row.item)"
                  >
                    Send Card
                  </b-button>
                </template>
              </b-table>

              <b-button
                v-if="hiddenSearchResultCount > 0 || showAllResults"
                size="sm"
                variant="link"
                class="show-more"
                @click="showAllResults = !showAllResults"
              >
                {{
                  showAllResults
                    ? "Show fewer"
                    : `+${hiddenSearchResultCount} more`
                }}
              </b-button>
            </div>
          </template>
        </b-card>

        <b-card v-if="shouldShowCamera" bg-variant="light" class="mt-3">
          <vue-qr-reader
            :paused="false"
            :track="true"
            :use-back-camera="true"
            :draw-on-found="true"
            :constraints="{ facingMode }"
            v-on:code-scanned="codeArrived"
            v-on:error-captured="errorCaptured"
            class="qr-reader-container"
          ></vue-qr-reader>

          <b-alert v-if="cameraError" variant="danger" show class="mt-3">
            {{ cameraError }}
          </b-alert>

          <b-row class="mt-3">
            <b-col cols="12" md="6" class="mb-2 mb-md-0">
              <b-button
                @click="switchCamera"
                type="button"
                variant="outline-primary"
                block
              >
                <b-icon icon="camera-fill" class="mr-2"></b-icon>
                Flip Camera
              </b-button>
            </b-col>
            <b-col cols="12" md="6">
              <b-button
                @click="toggleCamera"
                type="button"
                variant="primary"
                block
              >
                <b-icon icon="x-circle" class="mr-2"></b-icon>
                Close Camera
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-form>

      <b-form @submit.prevent="submitForm" id="dealer-form">
        <!-- Test Data Button (Development Only) -->
        <b-alert v-if="isDevelopmentMode" show variant="warning" class="mb-3">
          <b-button @click="fillTestData" variant="primary" size="sm">
            Fill Test Data (Auto-Renew)
          </b-button>
          <small class="ml-2"
            >Development Mode Only - Click to populate all fields</small
          >
        </b-alert>

        <!-- Every membership on the loaded account.
             This was once shown only for accounts holding more than one,
             because all it did was let the dealer pick which membership to
             renew. It now also carries the secondary contacts and boats, the
             boat status, and the warning that a record never reached
             Salesforce - none of which is about having several memberships,
             and all of which a single-membership account needs just as much.
             Most accounts have exactly one, so gating on that hid the sync
             warning from almost everybody. -->
        <b-card
          v-if="membershipContexts.length"
          bg-variant="light"
          class="mb-3"
        >
          <div class="memberships-heading">
            <span class="col-form-label-lg font-weight-bold">{{
              membershipsHeading
            }}</span>
            <small class="text-muted">{{ membershipsHint }}</small>
          </div>

          <MembershipPanel
            v-for="{ context, index } in visibleMembershipPanels"
            :key="context.membership.sfid || index"
            :context="context"
            :is-selected="index === selectedMembershipIndex"
            :selectable="membershipContexts.length > 1"
            @select="selectMembershipContext(index)"
            @toggle="context.expanded = !context.expanded"
            :account-contacts="accountContacts"
            :contact-record-type-id="contactRecordTypeId"
            :stripe-customer-id="existingStripeCustomerId"
            @arb-cancelled="onArbCancelled(context)"
            :boat-model="index === selectedMembershipIndex ? boats : null"
            :contact-model="index === selectedMembershipIndex ? contacts : null"
            @update-contact="updateContextContact(context, $event)"
            @update-boat="updateContextBoat(context, $event)"
            @set-contact="setContextContact(context, $event)"
            @add-contact="addContextContact(context, $event)"
            @add-boat="addContextBoat(context, $event)"
          />

          <b-button
            v-if="hiddenMembershipCount > 0 || showAllMemberships"
            size="sm"
            variant="link"
            class="show-more"
            @click="showAllMemberships = !showAllMemberships"
          >
            {{
              showAllMemberships
                ? "Show fewer"
                : `+${hiddenMembershipCount} more`
            }}
          </b-button>
        </b-card>

        <b-card bg-variant="light">
          <b-form-group
            label-cols-lg="3"
            label="Membership Questions"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
            <b-form-group label="Select Membership Type">
              <b-row>
                <b-col sm="4">
                  <b-form-radio-group
                    @change="updateCartPrice($event)"
                    class="text-nowrap mobile-friendly-radio"
                    id="card-selection-radio"
                    v-model="CardSelection"
                    :options="CardOptions"
                    name="cardtype-radio-options"
                    stacked
                  ></b-form-radio-group>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group
              v-if="
                !this.CardSelection.includes('Trial') &&
                !this.CardSelection.includes('Commercial')
              "
              label="Roadside Assistance When Trailering"
              id="trailering-type-label"
            >
              <b-form-radio-group
                class="mobile-friendly-radio"
                id="trailering-selection-radio"
                @change="updateCartPrice($event)"
                v-model="TrailerSelection"
                :options="TrailerOptions"
                name="trailering-radio-options"
                stacked
              ></b-form-radio-group>
            </b-form-group>

            <hr v-if="!this.CardSelection.includes('Trial')" />
            <b-form-group
              class="w-100"
              label-for="donation_amount"
              v-if="!this.CardSelection.includes('Trial')"
            >
              <p>
                <span v-html="donation_label"></span>
                <b-icon
                  icon="info-circle-fill"
                  v-b-tooltip.hover.html="donation_tooltip"
                  variant="info"
                  class="ml-2"
                  style="cursor: help"
                ></b-icon>
              </p>
              <b-input-group prepend="$" class="w-25">
                <b-form-input
                  id="donation_amount"
                  v-model="$v.donation_amount.$model"
                  @keypress="stripTheGarbage($event)"
                  @blur="formatDollars()"
                >
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-form-group>
        </b-card>

        <TrialCard
          v-if="this.CardSelection.includes('Trial')"
          :v="$v.selected_trial_time_product"
          :trial-options="getTrialOptions"
        />

        <ContactCard
          :v="$v.contacts"
          :account-name-v="$v.account.name"
          :is-renew="isRenew"
          :account-sfid="account_sfid"
          @fill-account-name="FillAccountName"
          @update-contact="updateContactInfo"
        />

        <AddressCard
          :v="$v.account"
          prefix="billing"
          label="Billing Address"
          @postal-blur="getPostalCodeInfo($event, 'billing')"
        >
          <template #actions>
            <b-form-checkbox
              id="shipping_same_as_billing"
              v-model="shipping_same_as_billing"
              name="shipping_same_as_billing-checkbox"
              value="true"
              unchecked-value="false"
            >
              Shipping Same As Billing
            </b-form-checkbox>

            <div
              v-if="
                isRenew &&
                (shipping_same_as_billing === 'true' ||
                  shipping_same_as_billing === true)
              "
            >
              <b-button
                class="update-button"
                style="margin-top: 15px"
                :disabled="
                  !isRenew &&
                  (shipping_same_as_billing !== 'true' ||
                    shipping_same_as_billing === false) &&
                  account_sfid !== undefined
                "
                @click="updateAddressInfo('both')"
                type="button"
                variant="primary"
                >Update Billing and Shipping Address</b-button
              >
            </div>
            <div v-else-if="isRenew === true">
              <div>
                <b-button
                  style="margin-top: 15px"
                  :disabled="
                    !isRenew &&
                    (shipping_same_as_billing !== 'true' ||
                      shipping_same_as_billing === false) &&
                    account_sfid !== undefined
                  "
                  @click="updateAddressInfo('billing')"
                  type="button"
                  variant="primary"
                  >Update Billing Address</b-button
                >
              </div>
            </div>
          </template>
        </AddressCard>

        <AddressCard
          v-if="
            this.shipping_same_as_billing == 'false' ||
            this.shipping_same_as_billing == false
          "
          :v="$v.account"
          prefix="shipping"
          label="Shipping Address"
          @postal-blur="getPostalCodeInfo($event, 'shipping')"
        >
          <template #actions>
            <div v-if="isRenew && shipping_same_as_billing === 'false'">
              <b-button
                :disabled="
                  !isRenew &&
                  (shipping_same_as_billing !== 'true' ||
                    shipping_same_as_billing === false) &&
                  account_sfid !== undefined
                "
                @click="updateAddressInfo('shipping')"
                type="button"
                variant="primary"
                >Update Shipping Address</b-button
              >
            </div>
          </template>
        </AddressCard>

        <BoatInfoCard
          :v="$v.boats"
          :aor-field-v="$v.aor_display_field"
          :marina-desc="marina_desc"
          :current-year-add-one="current_year_add_one"
          :is-renew="isRenew"
          :shipping-same-as-billing="shipping_same_as_billing"
          :account-sfid="account_sfid"
          @assign-aor="assignAOR"
          @update-boat="updateBoatInfo"
        />

        <b-card bg-variant="light" v-if="!this.CardSelection.includes('Trial')">
          <b-form-group
            label-cols-sm="12"
            label-cols-md="3"
            label-cols-lg="3"
            label="Codes"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
            <b-form-group
              v-if="!this.CardSelection.includes('Trial')"
              label="Activation Code:"
              label-for="activation-code"
              valid-feedback="Activation code successfully applied"
            >
              <ActivationCodeInput
                @apply-code="handleActivationCode"
                @clear-code="resetActivationCodes"
                ref="activationCodeInput"
              />
            </b-form-group>

            <b-form-group
              v-if="!this.CardSelection.includes('Trial')"
              label="Promotion Code:"
              label-for="promotion-code"
              :state="promotionstate"
              :invalid-feedback="promotionFeedback"
              valid-feedback="Promotion code successfully applied"
            >
              <b-row v-if="!this.CardSelection.includes('Trial')">
                <b-col cols="12" md="8">
                  <b-form
                    @submit.prevent="submitPromo(promotion_code)"
                    id="promotion_code"
                  >
                    <b-form-input
                      id="promotion-code"
                      v-model="$v.promotion_code.$model"
                      :state="promotionstate"
                      trim
                    >
                    </b-form-input>

                    <b-form-checkbox
                      id="do-not-clear-promo-checkbox"
                      v-model="do_not_clear_promo"
                      name="do-not-clear-promo-checkbox"
                      value="true"
                      unchecked-value="false"
                    >
                      Keep promotion after submission or clear
                    </b-form-checkbox>
                  </b-form>
                </b-col>
                <b-col cols="12" md="4" class="mt-3 mt-md-0">
                  <b-button-toolbar>
                    <div>
                      <b-button-group>
                        <b-button
                          :disabled="
                            this.promotion_valid &&
                            !this.promotion_code.length <= 1
                          "
                          @click="submitPromo(promotion_code)"
                          type="button"
                          variant="primary"
                          >Apply</b-button
                        ></b-button-group
                      >

                      <b-button-group class="mx-1">
                        <b-button
                          @click="resetPromotionDetails()"
                          type="button"
                          variant="outline-secondary"
                          >Clear</b-button
                        >
                      </b-button-group>
                    </div>
                  </b-button-toolbar>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group
              label="Campaign:"
              label-for="campaign"
              :state="campaignstate"
              :invalid-feedback="campaignFeedback"
              valid-feedback="Campaign successfully applied"
              v-if="!this.CardSelection.includes('Trial')"
            >
              <b-row>
                <b-col cols="12" md="8">
                  <b-form
                    @submit.prevent="submitCampaign(campaign)"
                    id="promotion_code"
                  >
                    <b-form-input
                      id="campaign"
                      v-model="$v.campaign.$model"
                      :state="campaignstate"
                      trim
                    >
                    </b-form-input>
                  </b-form>
                </b-col>
                <b-col cols="12" md="4" class="mt-3 mt-md-0">
                  <b-button-toolbar>
                    <div>
                      <b-button-group>
                        <b-button
                          @click="submitCampaign(campaign)"
                          type="button"
                          variant="primary"
                          >Apply</b-button
                        ></b-button-group
                      >

                      <b-button-group class="mx-1">
                        <b-button
                          @click="resetCampaignDetails()"
                          type="button"
                          variant="outline-secondary"
                          >Clear</b-button
                        >
                      </b-button-group>
                    </div>
                  </b-button-toolbar>
                </b-col>
              </b-row>
            </b-form-group>
          </b-form-group>
        </b-card>

        <b-card bg-variant="light">
          <b-form-group
            label-cols-lg="3"
            label="Payment Information"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
            <b-form-group>
              <b-form-row>
                <b-col>
                  <div id="tooptip-test">
                    <b-form-checkbox
                      id="funds-collected-locally-checkbox"
                      v-model="funds_collected_locally"
                      name="fundscollectedlocally"
                      value="true"
                      unchecked-value="false"
                      :disabled="isFSI"
                      @change="
                        autorenew_status = false;
                        arb_value = false;
                        if (promotion_arb_required) {
                          resetPromotionDetails();
                        }
                      "
                    >
                      Cash or Check (Funds Collected Locally)
                      <b-icon
                        icon="info-circle-fill"
                        class="ml-2"
                        v-b-tooltip.hover
                        :title="this.tooltip_display_text"
                        variant="info"
                        style="cursor: help"
                      ></b-icon>
                    </b-form-checkbox>
                    <div
                      v-if="
                        funds_collected_locally === true ||
                        funds_collected_locally === 'true'
                      "
                      class="text-danger font-weight-bold mt-2"
                    >
                      THIS MEMBER WILL NOT BE CHARGED BECAUSE YOU HAVE SELECTED
                      Funds Collected Locally (Cash or Check).
                    </div>
                  </div>
                </b-col>
              </b-form-row>
            </b-form-group>

            <!-- Stripe Payment Form -->
            <div v-if="this.CreditCardRequired">
              <!-- Show Pay button if payment form hasn't been initialized -->
              <b-form-row v-if="!stripePaymentInitialized">
                <b-col>
                  <b-button
                    variant="primary"
                    size="lg"
                    @click="initializeStripePayment"
                    :disabled="payButtonDisabledReason !== null"
                  >
                    {{
                      shouldUseSetupIntent
                        ? "Set Up Subscription"
                        : price_total != null
                        ? "Collect Card Info"
                        : "Loading..."
                    }}
                  </b-button>
                  <b-alert
                    v-if="payButtonDisabledReason"
                    show
                    variant="warning"
                    class="mt-3 mb-3 inline-notice"
                  >
                    <b-icon
                      icon="exclamation-triangle-fill"
                      class="mr-2"
                    ></b-icon>
                    {{ payButtonDisabledReason }}
                  </b-alert>
                </b-col>
              </b-form-row>

              <!-- Show Stripe Payment Form once initialized -->
              <b-form-row v-if="stripePaymentInitialized">
                <b-col>
                  <StripePaymentForm
                    ref="stripePaymentForm"
                    :amount="shouldUseSetupIntent ? 0 : price_total * 100"
                    :useSetupIntent="shouldUseSetupIntent"
                    :customerEmail="contacts.email"
                    :customerName="`${contacts.firstname} ${contacts.lastname}`"
                    :existingCustomerId="existingStripeCustomerId"
                    :autoMount="false"
                    @card-complete="isStripeCardComplete = $event"
                  />
                  <span
                    v-if="
                      $v.isStripeCardComplete.$invalid &&
                      $v.isStripeCardComplete.$dirty
                    "
                    class="text-danger"
                  >
                    Please complete your card information.
                  </span>
                </b-col>
              </b-form-row>
            </div>

            <b-form-row>
              <b-col>
                <b-form-checkbox
                  id="auto-renew-checkbox"
                  v-model="arb_value"
                  :disabled="!this.show_display_auto_renew"
                  name="auto-renew-checkbox"
                  :value="true"
                  :unchecked-value="false"
                  @change="handleActivationCode(activation_codes?.name)"
                >
                  Automatically Renew The Membership Each Year
                  <b-icon
                    icon="info-circle-fill"
                    class="ml-2"
                    v-b-tooltip.hover
                    title="Make sure to read the disclaimer directly below if customer wants auto renewal."
                    variant="info"
                    style="cursor: help"
                  ></b-icon>
                </b-form-checkbox>
              </b-col>
            </b-form-row>

            <b-form-row v-if="isRenew && arb_value && !isAlreadyEnrolledInArb">
              <b-col class="pl-4 mt-2">
                <b-form-checkbox
                  id="arb-enroll-only-checkbox"
                  v-model="arb_enroll_only"
                  :value="true"
                  :unchecked-value="false"
                >
                  Enroll in auto-renew only &mdash; don't take payment today
                  <b-icon
                    icon="info-circle-fill"
                    class="ml-2"
                    v-b-tooltip.hover
                    title="Saves the customer's card and schedules the auto-renew to start on their current membership expiration date. No charge happens today."
                    variant="info"
                    style="cursor: help"
                  ></b-icon>
                </b-form-checkbox>
              </b-col>
            </b-form-row>

            <div v-if="autorenew_status == 'true' || autorenew_status == true">
              <p v-html="autorenew_disclaimer"></p>
            </div>
          </b-form-group>
        </b-card>

        <b-card bg-variant="light">
          <b-form-group
            label-cols-lg="3"
            label="Miscellaneous"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
            <b-form-row>
              <b-col>
                <b-form-group id="do-not-clear-email" label-for="CClist">
                  <template #label>
                    Additional CC list
                    <b-icon
                      icon="info-circle-fill"
                      class="ml-2"
                      variant="info"
                      style="cursor: help"
                      v-b-tooltip.hover="
                        'Comma separated value. Example: pat@test.com,joe@test.com'
                      "
                    />
                  </template>
                  <b-form-input
                    id="CClist"
                    v-model="$v.additional_cc_list.$model"
                  >
                  </b-form-input>

                  <span
                    v-if="$v.additional_cc_list.$invalid"
                    class="text-danger"
                    >Emails must be valid (x@y.z) AND this input must not have
                    trailing comma AND email must be a @seatow.com email
                    addreess
                  </span>
                </b-form-group>
              </b-col>
              <b-col> </b-col>
            </b-form-row>

            <b-form-checkbox
              id="do-not-clear-email-checkbox"
              v-model="do_not_clear_cc"
              name="do-not-clear-email-checkbox"
              value="true"
              unchecked-value="false"
              class="mb-4"
            >
              Keep CC box after submission or clear
            </b-form-checkbox>

            <b-row>
              <b-col>
                Referring Account:
                <Dropdown
                  ref="dropdown"
                  style="padding-top: 10px"
                  :options="referringAccountOptions"
                  v-on:selected="validateSelection"
                  v-on:filter="getDropdownValues"
                  :disabled="false"
                  placeholder="Tap here to search for a referring account"
                ></Dropdown>
                <div
                  v-if="
                    $v.referring_account.$dirty &&
                    !$v.referring_account.required
                  "
                  class="text-danger"
                  style="font-size: 0.875rem"
                >
                  Referring account is required if providing a trial
                </div>
              </b-col>
              <b-button
                v-if="referring_account"
                @click="clearReferringAccount()"
                type="button"
                variant="primary"
                >Clear</b-button
              >
            </b-row>
          </b-form-group>
        </b-card>

        <b-card bg-variant="light">
          <b-form-group
            label-cols-sm="12"
            label-cols-md="2"
            label-cols-lg="2"
            label="Summary"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
          </b-form-group>

          <div class="order-summary">
            <!-- Base Membership Section -->
            <div class="summary-section">
              <div class="summary-item">
                <div class="item-label">
                  <b-icon
                    icon="card-checklist"
                    class="mr-2 text-primary"
                  ></b-icon>
                  <span v-if="!this.CardSelection.includes('Trial')"
                    >{{ this.readableCardName(this.CardSelection) }} Card</span
                  >
                  <span v-if="this.CardSelection.includes('Trial')"
                    >Gold Card ({{ getTrialDays }}-Day Free Trial)</span
                  >
                </div>
                <div class="item-value">
                  <span v-if="!this.CardSelection.includes('Trial')">
                    ${{
                      card_price != null
                        ? (Math.round(card_price * 100) / 100).toFixed(2)
                        : "0.00"
                    }}
                    <span class="text-muted small">/year</span>
                  </span>
                  <span
                    v-if="this.CardSelection.includes('Trial')"
                    class="text-success font-weight-bold"
                  >
                    FREE
                  </span>
                </div>
              </div>
              <div
                class="item-description"
                v-if="this.CardSelection.includes('Trial') && arb_value"
              >
                After {{ getTrialDays }} days, converts to paid Gold Card
                membership at ${{
                  goldCardPrice != null
                    ? (Math.round(goldCardPrice * 100) / 100).toFixed(2)
                    : "0.00"
                }}/year
              </div>
            </div>

            <!-- Add-ons Section -->
            <div
              class="summary-section"
              v-if="
                TrailerSelection != 'None' ||
                (this.donation_amount != 0.0 &&
                  !this.CardSelection.includes('Trial'))
              "
            >
              <div class="section-divider"></div>
              <div class="section-title">Add-ons</div>

              <div class="summary-item" v-if="TrailerSelection != 'None'">
                <div class="item-label">
                  <b-icon icon="truck" class="mr-2 text-primary"></b-icon>
                  <span>{{
                    TrailerSelection === "Marine"
                      ? "Trailer Care"
                      : TrailerSelection === "Universal"
                      ? "Trailer Care Plus"
                      : TrailerSelection
                  }}</span>
                </div>
                <div class="item-value item-addition">
                  +${{
                    trailering_price != null
                      ? (Math.round(trailering_price * 100) / 100).toFixed(2)
                      : "0.00"
                  }}
                  <span class="text-muted small">/year</span>
                </div>
              </div>

              <div
                class="summary-item"
                v-if="
                  this.donation_amount != 0.0 &&
                  !this.CardSelection.includes('Trial')
                "
              >
                <div class="item-label">
                  <b-icon icon="heart-fill" class="mr-2 text-danger"></b-icon>
                  <span>Sea Tow Foundation Donation</span>
                </div>
                <div class="item-value item-addition">
                  +${{
                    donation_amount != null
                      ? parseFloat(donation_amount).toFixed(2)
                      : "0.00"
                  }}
                </div>
              </div>
            </div>

            <!-- Discounts Section -->
            <div
              class="summary-section"
              v-if="
                this.promotion_valid ||
                this.activation_codes.value__c > 0 ||
                (funds_collected_locally && this.promotion_code != 'FSI')
              "
            >
              <div class="section-divider"></div>
              <div class="section-title">Discounts</div>

              <!-- Promotion Code -->
              <div v-if="this.promotion_valid">
                <div class="summary-item">
                  <div class="item-label">
                    <b-icon icon="tag-fill" class="mr-2 text-success"></b-icon>
                    <span>{{ this.promotion_code }}</span>
                  </div>
                  <div
                    class="item-value item-discount"
                    v-if="
                      this.promotion_type__c == 'Percentage Value Promotion' ||
                      this.promotion_type__c == 'Percentage_Value_Promotion'
                    "
                  >
                    {{ this.promotion_value_percentage_discount }}% off
                  </div>
                  <div
                    class="item-value item-discount"
                    v-if="
                      this.promotion_type__c == 'Dollar_Value_Promotion' ||
                      this.promotion_type__c == 'Dollar Value Promotion'
                    "
                  >
                    -${{
                      promotion_price != null
                        ? (Math.round(promotion_price * 100) / 100).toFixed(2)
                        : "0.00"
                    }}
                  </div>
                </div>
                <div class="item-description" v-if="promotion_desc2">
                  {{ promotion_desc2 }}
                </div>
              </div>

              <!-- Activation Code -->
              <div v-if="this.activation_codes.value__c > 0">
                <div class="summary-item">
                  <div class="item-label">
                    <b-icon icon="key-fill" class="mr-2 text-success"></b-icon>
                    <span>{{ this.activation_codes.name }}</span>
                  </div>
                  <div class="item-value item-discount">
                    -${{
                      card_price != null && activation_codes.value__c != null
                        ? (
                            Math.round(
                              (activation_codes.value__c > card_price
                                ? card_price
                                : activation_codes.value__c) * 100
                            ) / 100
                          ).toFixed(2)
                        : "0.00"
                    }}
                  </div>
                </div>
                <div
                  class="item-description"
                  v-if="
                    activation_codes.value__c != null &&
                    card_price != null &&
                    trailering_price != null &&
                    activation_codes.value__c > card_price + trailering_price
                  "
                >
                  (${{ activation_codes.value__c.toFixed(2) }} total card value)
                </div>
              </div>

              <!-- Funds Collected Locally -->
              <div
                v-if="
                  (funds_collected_locally === true ||
                    funds_collected_locally === 'true') &&
                  this.promotion_code != 'FSI'
                "
              >
                <div class="summary-item">
                  <div class="item-label">
                    <b-icon icon="cash-coin" class="mr-2 text-success"></b-icon>
                    <span>Funds Collected Locally</span>
                  </div>
                  <div class="item-value item-discount">
                    -${{
                      price_total != null
                        ? (Math.round(price_total * 100) / 100).toFixed(2)
                        : "0.00"
                    }}
                  </div>
                </div>
                <div class="item-description">Payment collected locally</div>
              </div>
            </div>

            <!-- Campaign Section -->
            <div class="summary-section" v-if="this.campaign_state_value">
              <div class="section-divider"></div>
              <div class="summary-item">
                <div class="item-label">
                  <b-icon icon="megaphone-fill" class="mr-2 text-info"></b-icon>
                  <span>Campaign: {{ this.campaign }}</span>
                </div>
              </div>
              <div class="item-description">{{ this.campaign_desc }}</div>
            </div>

            <!-- Referring Account Section -->
            <div class="summary-section" v-if="this.referring_account">
              <div class="section-divider"></div>
              <div class="summary-item">
                <div class="item-label">
                  <b-icon icon="person-badge" class="mr-2 text-info"></b-icon>
                  <span>Referring Account</span>
                </div>
              </div>
              <div class="item-description">
                {{ this.$refs["dropdown"].searchFilter }} ({{
                  this.referring_account
                }})
              </div>
            </div>

            <!-- Auto-Renew Section -->
            <div class="summary-section">
              <div class="section-divider"></div>
              <div class="summary-item">
                <div class="item-label">
                  <b-icon
                    :icon="arb_value ? 'arrow-repeat' : 'arrow-right'"
                    :class="
                      arb_value ? 'mr-2 text-success' : 'mr-2 text-secondary'
                    "
                  ></b-icon>
                  <span>Auto-Renew</span>
                </div>
                <div class="item-value">
                  <b-badge :variant="arb_value ? 'success' : 'secondary'">
                    {{ arb_value ? "Enabled" : "Disabled" }}
                  </b-badge>
                </div>
              </div>
              <div class="item-description" v-if="arb_value">
                Membership will automatically renew each year
              </div>
            </div>

            <!-- Total Section -->
            <div class="summary-section">
              <div class="section-divider-bold"></div>

              <!-- Single Total (No Auto-Renew) -->
              <div class="summary-total" v-if="!arb_value">
                <div class="total-label">Total Price</div>
                <div class="total-value">
                  ${{
                    price_total != null
                      ? (Math.round(price_total * 100) / 100).toFixed(2)
                      : "0.00"
                  }}
                </div>
              </div>

              <!-- Breakdown Total (With Auto-Renew) -->
              <div v-if="arb_value">
                <div class="summary-total-breakdown">
                  <div class="breakdown-item">
                    <div class="breakdown-label">
                      <b-icon
                        icon="calendar-check"
                        class="mr-2 text-primary"
                      ></b-icon>
                      <span>Total Today</span>
                    </div>
                    <div class="breakdown-value">
                      ${{
                        totalDueToday != null
                          ? (Math.round(totalDueToday * 100) / 100).toFixed(2)
                          : "0.00"
                      }}
                    </div>
                  </div>
                  <div class="breakdown-item recurring">
                    <div class="breakdown-label">
                      <b-icon
                        icon="arrow-repeat"
                        class="mr-2 text-success"
                      ></b-icon>
                      <span v-if="!this.CardSelection.includes('Trial')"
                        >Then Annually</span
                      >
                      <span v-if="this.CardSelection.includes('Trial')"
                        >After {{ getTrialDays }}-Day Trial</span
                      >
                    </div>
                    <div class="breakdown-value">
                      <span v-if="!this.CardSelection.includes('Trial')">
                        ${{
                          recurringAnnualPrice != null
                            ? (
                                Math.round(recurringAnnualPrice * 100) / 100
                              ).toFixed(2)
                            : "0.00"
                        }}
                      </span>
                      <span v-if="this.CardSelection.includes('Trial')">
                        ${{
                          goldCardPrice != null
                            ? (
                                Math.round(
                                  (goldCardPrice + (trailering_price || 0)) *
                                    100
                                ) / 100
                              ).toFixed(2)
                            : "0.00"
                        }}
                      </span>
                      <span class="text-muted small">/year</span>
                    </div>
                  </div>
                  <div
                    class="item-description text-muted small"
                    v-if="this.CardSelection.includes('Trial')"
                  >
                    First payment in {{ getTrialDays }} days, then annually
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- <label for="by continuing">
            By continuing, the user verifies that their boats are in good
            working order and understand that Dock-to-Dock tows from the boat’s
            home port to a repair facility, a ramp for haul out or similar, are
            not covered for the first 30 days after membership
            activation.</label
          > -->
        </b-card>
        <p />
        <b-card bg-variant="light">
          <b-form-group
            label-cols-sm="12"
            label-cols-md="2"
            label-cols-lg="2"
            label="Verify"
            label-size="lg"
            label-class="font-weight-bold pt-0"
            class="mb-0"
          >
          </b-form-group>

          <div class="verify-content">
            <h6 class="verify-heading">
              By hitting SUBMIT you verify that you did the following:
            </h6>
            <b-list-group>
              <b-list-group-item
                >All boats are in good working order</b-list-group-item
              >
              <b-list-group-item
                >Informed customer that membership is effective 24 hours after
                payment received
              </b-list-group-item>
              <b-list-group-item
                >Dock-to-Dock Tows are not covered within the first 30 days
                after membership activation</b-list-group-item
              >
              <b-list-group-item
                >Informed Customer of Automatic Renewal Program
                Guidelines</b-list-group-item
              >
            </b-list-group>

            <b-button
              type="submit"
              :disabled="this.disableSubmit"
              variant="primary"
              size="lg"
              block
              class="submit-button mt-3"
              >Submit</b-button
            >
          </div>
        </b-card>
      </b-form>
    </div>
  </b-container>
</template>
<script>
import ActivationCodeInput from "@/components/ActivationCodeInput.vue";
import Dropdown from "./Dropdown.vue";
import VueQrReader from "vue-qr-reader/dist/lib/vue-qr-reader.umd.js";
import PromoCodeDiscrepancyModal from "./PromoCodeDiscrepancyModal.vue";
import StripePaymentForm from "./StripePaymentForm.vue";

import { validateActivationCode } from "@/services/api";
import { validatePromo } from "@/services/api/promo";
import {
  insertStripeCustomerPaymentMethod,
  manageSubscriptionSchedule,
} from "@/services/api/stripe";
import {
  getAllMembershipInfo,
  patchMembership,
} from "@/services/api/membership";
import { patchAccount } from "@/services/api/account";
import { patchContact, postContact } from "@/services/api/contact";
import { patchBoat, postBoat } from "@/services/api/boat";
import {
  searchMembership,
  searchByZip,
  getAor,
  getAorFranchiseeLink,
  searchAccounts,
  getAorContactEmailBySfid,
  resendCard,
} from "@/services/api/utility";
import { truthyBoolean } from "@/utils/truthyBoolean";
import { formatCalendarDate } from "@/utils/formatDate";
import { log as devLog, error as devError } from "@/utils/logger";
import { customEmail, pureHelperMethods } from "./dealerForm/helpers";

// Re-export so this module's public API is unchanged.
export { sleep } from "./dealerForm/helpers";

import {
  required,
  maxLength,
  minLength,
  integer,
  requiredIf,
} from "vuelidate/lib/validators";

import auth from "../services/auth";

// Domain method clusters live in mixins to keep this file navigable. They merge
// into this instance, so every `this.x` inside them resolves exactly as before.
import submissionMixin from "./dealerForm/mixins/submissionMixin";
import submitFormMixin from "./dealerForm/mixins/submitFormMixin";
import renewalMixin from "./dealerForm/mixins/renewalMixin";
import catalogMixin from "./dealerForm/mixins/catalogMixin";
import promoCampaignMixin from "./dealerForm/mixins/promoCampaignMixin";
import uxcamMixin from "./dealerForm/mixins/uxcamMixin";
import MembershipPanel from "./dealerForm/MembershipPanel.vue";
import { boatValidations } from "./dealerForm/boatValidation";

// How much of a long list is shown before the dealer asks for the rest. Both
// lists sit above the form, so rendering them in full pushes the thing the
// dealer came to fill in off the bottom of the screen.
const SEARCH_RESULT_PREVIEW = 5;
const MEMBERSHIP_PREVIEW = 3;

import {
  isValidEmail,
  isValidNanpPhone,
  normalizePhone,
} from "./dealerForm/contactValidation";
import {
  coerceAccountField,
  coerceBoatField,
  coerceContactField,
} from "./dealerForm/loadedRecord";
import { toStateCode } from "@/config/usStates";
import { RECORDTYPE_PERSON_CONTACT } from "@/config/salesforceIds";

import BoatInfoCard from "./dealerForm/BoatInfoCard.vue";
import ContactCard from "./dealerForm/ContactCard.vue";
import AddressCard from "./dealerForm/AddressCard.vue";
import TrialCard from "./dealerForm/TrialCard.vue";

export default {
  async mounted() {
    // Load product catalog first
    await this.loadProductCatalog();

    this.cachedName = this.setCachedName();
    console.log(this.cachedName);
  },
  watch: {
    promotion_code(newValue, oldValue) {
      const becameFSI = newValue?.toUpperCase() === "FSI";
      const wasFSI = oldValue?.toUpperCase() === "FSI";
      if (becameFSI) {
        this.funds_collected_locally = true;
      } else if (wasFSI) {
        this.funds_collected_locally = false;
      }
    },
    CardSelection(newValue) {
      console.log("CardSelection changed to:", newValue);
      console.log("isRenew:", this.isRenew);
      console.log("isLoadingMembership:", this.isLoadingMembership);
      console.log("includes Trial:", newValue && newValue.includes("Trial"));

      // Skip validation if loading membership data programmatically
      if (this.isLoadingMembership) {
        console.log("Skipping trial validation - loading membership data");
        this.previousCardSelection = newValue;
        return;
      }

      // Validate trial eligibility when Trial is selected
      if (newValue && newValue.includes("Trial") && this.isRenew) {
        console.log("Trial selected for renewal, checking eligibility...");
        const eligible = this.checkTrialEligibility();
        console.log("Trial eligibility result:", eligible);

        if (!eligible) {
          // Show modal and prevent selection
          console.log("Not eligible, showing modal");
          this.$nextTick(() => {
            this.$refs["trial-eligibility-modal"].show();
          });
        } else {
          // Trial is allowed, update previous selection
          console.log("Trial is eligible, allowing selection");
          this.previousCardSelection = newValue;
        }
      } else {
        // Not a trial, update previous selection
        console.log(
          "Not a trial or not a renewal, updating previous selection"
        );
        this.previousCardSelection = newValue;
      }

      // Re-validate promotion code against the new card selection
      if (this.promotion_valid && this.promotion_valid_on_membership__c) {
        if (!this.validatePromoMembershipType()) {
          this.resetPromotionDetails();
        }
      }
    },
    aor_display_field(newValue) {
      if (newValue) {
        this.getAORContactEmail();
      }
    },
    arb_value(oldValue, newValue) {
      console.log("oldValue " + oldValue);
      console.log("newValue " + newValue);
      //honestly this is mind fucking me, idk why logic is this way but it works
      if (newValue === true || newValue === "true") {
        console.log("New value is false, we do not want to display the error");
        this.show_display_auto_renew;
        this.checkbox_display;
        this.autorenew_status = false;
        console.log("autorenew_status:", this.autorenew_status); // Add this line for debugging
      } else {
        console.log("New value is true, we want to display the error");
        this.show_display_auto_renew;
        this.checkbox_display;
        this.autorenew_status = true;
        console.log("autorenew_status:", this.autorenew_status); // Add this line for debugging      }
      }
    },
    async CreditCardRequired(newValue, oldValue) {
      // When credit card requirement changes, manage Stripe payment intent
      console.log(`CreditCardRequired changed from ${oldValue} to ${newValue}`);

      if (!newValue && oldValue) {
        // Credit card no longer required - clear the Stripe session and reset initialization flag
        console.log("Credit card no longer required, clearing Stripe session");
        this.stripePaymentInitialized = false;
        // Only call clearStripeSession if the component exists and has been initialized
        if (
          this.$refs.stripePaymentForm &&
          typeof this.$refs.stripePaymentForm.clearStripeSession === "function"
        ) {
          try {
            await this.$refs.stripePaymentForm.clearStripeSession();
          } catch (error) {
            console.warn(
              "Error clearing Stripe session in watcher:",
              error.message
            );
          }
        }
      } else if (newValue && !oldValue) {
        // Credit card now required - reset initialization flag so Pay button shows
        console.log("Credit card now required, showing Pay button");
        this.stripePaymentInitialized = false;
      }
    },
  },
  data() {
    return {
      showPromoCodeModal: false,
      tempFormPromoCode: "",
      renewalPromoCode: "",
      activation_codes: {
        code: null,
        card_status__c: null,
        code_type__c: null,
        name: null,
        related_product__c: null,
        related_program__c: null,
        usage_rules__c: null,
        valid_on_membership__c: null,
        valid_on_business_type__c: null,
        value__c: null,
      },
      aor_contact_email: null,
      arb_value: false,
      arb_enroll_only: false,
      referringAccountOptions: [],
      referring_account: null,
      facingMode: "environment",
      noRearCamera: false,
      noFrontCamera: false,
      shouldShowCamera: false,
      cameraError: null,
      aor_display_field: "999 (International)",
      cachedName: null,
      disableSubmit: false,
      membership_status__c: null,
      memno: null,
      do_not_clear_cc: false,
      do_not_clear_promo: "false",
      do_not_clear_referral: "false",
      additional_cc_list: "",
      membership_expiration_date__c: null,
      old_aor: null,
      refTransId: null,
      auth_code: null,
      transId: null,
      cc_declined: false,
      stripePaymentIntentId: null,
      pendingOppGuid: null,
      stripeCustomerId: null,
      stripePaymentMethodId: null,
      existingStripeCustomerId: null, // Existing customer from Salesforce
      existingStripeCustomerSfid: null, // Salesforce ID of existing Stripe customer record
      isStripeCardComplete: false,
      stripeCardBrand: null,
      stripeCardLast4: null,
      stripeExpMonth: null,
      stripeExpYear: null,
      stripePaymentInitialized: false, // Tracks if Stripe payment form has been initialized
      // Product Catalog from API
      productCatalog: null, // Stores response from /utility/stripeproducts
      productsLoaded: false, // Tracks if catalog has been loaded
      productsLoading: false, // Tracks if catalog is currently being fetched
      productLoadError: null, // Stores any error from catalog load
      productCatalogPromise: null, // Stores the promise to prevent duplicate calls
      autorenew_disclaimer: `<b>If Automatic Renewal is selected you MUST do the following:</b><br><ul><li>Get verbal agreement from the customer that they are opting in to Automatic Renewal and understand that their Sea Tow membership will renew automatically each year on the expiration date at the then current membership rate, in accordance with the Sea Tow Automatic Renewal Program Agreement available at <a href="https://seatow.com" target="_blank">seatow.com</a>. They also may opt out anytime through <a href="https://seatow.com" target="_blank">seatow.com</a>, the Sea Tow app, by phone at <a href="tel:800-473-2869">800-473-2869</a>, or by emailing <a href="mailto:info@seatow.com">info@seatow.com</a>.</li></ul>`,
      donation_amount: 0.0,
      donation_label:
        "Support safer boating by making a donation to the <a href='https://www.seatow.com/tools-and-education/foundation'>Sea Tow Foundation</a>",
      donation_tooltip:
        "Founded in 2007, the Sea Tow Foundation is a public service organization with 501(c)3 nonprofit status whose mission is to promote safe boating practices that directly reduce fatalities, incidents and property damage related to recreational boating.",
      next_year: new Date().getFullYear() + 1,
      selected_trial_time_product: null,
      contacts: {
        firstname: null,
        lastname: null,
        email: null,
        mobilephone: null,
        phone: null,
      },
      account: {
        billingcity: null,
        billingcountry: "United States",
        billingpostalcode: null,
        billingstate: null,
        billingstreet: null,
        shippingcity: null,
        shippingcountry: "United States",
        shippingpostalcode: null,
        shippingstate: null,
        shippingstreet: null,
        name: "",
        //already_migrated__c: false,
      },
      boats: {
        color__c: null,
        aor__c: "a0d37000004fpm9AAA",
        name__c: null,
        boat_make__c: null,
        boat_status__c: null,
        engine_type__c: "Unspecified",
        fuel_type__c: null,
        hin__c: null,
        home_port_city__c: null,
        home_port_state__c: null,
        home_port_country__c: "United States",
        home_port_type__c: null,
        marina_name__c: null,
        length__c: null,
        number_of_engines__c: null,
        primary_boat__c: null,
        registration_document__c: null,
        year__c: null,
        boat_transfer_type__c: null,
      },
      memberships: {
        auto_renew__c: null,
        card_expiration_date__c: null,
        card_name__c: null,
        card_number__c: null,
        card_security_code__c: null,
        card_type__c: null,
        referral_credit_amount__c: null,
        trailer_care_type__c: null,
        trial_issued_date__c: null,
        next_trial_eligible_in_days__c: null,
        active_arb_card_type: null,
        active_arb_last_4_digits: null,
        active_arb_subscription_start_date: null,
      },
      arbs: {
        createddate: null,
        heroku_external_id__c: null,
        name: null,
        pymt__authnet_subscription_id__c: null,
        pymt__amount__c: null,
        pymt__account__c: null,
        pymt__opportunity__r__heroku_external_id__c: null,
        pymt__account__r__heroku_external_id__c: null,
        pymt__billing_city__c: null,
        pymt__billing_company__c: null,
        pymt__billing_country__c: null,
        pymt__billing_email__c: null,
        pymt__billing_first_name__c: null,
        pymt__billing_last_name__c: null,
        pymt__billing_phone__c: null,
        pymt__billing_postal_code__c: null,
        pymt__billing_state__c: null,
        pymt__billing_street__c: null,
        pymt__card_type__c: null,
        pymt__contact__c: null,
        pymt__contact__r__heroku_external_id__c: null,
        pymt__frequency__c: null,
        pymt__last_4_digits__c: null,
        pymt__opportunity__c: null,
        pymt__period__c: null,
        pymt__processor_connection__c: null,
        pymt__subscription_start_date__c: null,
        pymt__subscription_status__c: null,
        pymt__total_occurrences__c: null,
        sfid: null,
        systemmodstamp: null,
      },
      routes: {
        contacts: null,
        boats: null,
        memberships: null,
        account: null,
        arbs: null,
      },
      sfid: {
        contact: null,
        boat: null,
        membership: null,
        account: null,
      },
      campaign: "",
      card_number__c: null,
      card_expiration_month: null,
      card_expiration_year: null,
      promotion_state_value: null,
      campaign_state_value: null,
      campaign_desc: null,
      campaign_good_on: null,
      campaign_sfid: null,
      contact_sfid: null,
      boat_sfid: null,
      membership_sfid: null,
      account_sfid: null,
      renewal: false,
      profile_data: null,
      promotion_value_in_dollars: 0,
      promotion_type__c: null,
      promotion_valid_on_type: null,
      promotion_title: null,
      promotion_ongoing_promotion_type: null,
      promotion_arb_required: false,
      promotion_value_in_days: null,
      promotion_value_percentage_discount: null,
      promotion_desc: null,
      promotion_valid_on_membership__c: null,
      related_product_details: null,
      promotion_code: null,
      promotion_valid: false,
      campaign_valid: false,
      promotion_price: "",
      jwt: null,
      submitStatus: null,
      firstName: "",
      lastName: "",
      email: null,
      secondaryPhone: null,
      street: null,
      street2: null,
      city: null,
      state: null,
      zipcode: null,
      country: null,
      boat_length: null,
      boat_make: null,
      boat_doc: null,
      boat_kept_at: null,
      boat_loc_city: null,
      boat_loc_state: null,
      boat_loc_country: null,
      autorenew_status: false,
      funds_collected_locally: false,
      shipping_same_as_billing: true,
      card_price: null, // Will be set from productCatalog after load
      card_desc:
        "The choice of over 95% of Sea Tow members. This card provides membership benefits for any recreational vessel that has an engine and is registered to or owned by the member (covered vessels). Any person operating a covered vessel is entitled to receive membership benefits for that vessel. The Gold Card member may also use his/her privileges on any vessel he/she charters, rents, leases or borrows. For complete details on all Gold Card member privileges please see our Membership Agreement.",
      trailering_price: null,
      price_total: null, // Will be calculated after productCatalog loads
      isHomeportFlorida: false,
      CardSelection: "Gold",
      previousCardSelection: "Gold", // Track previous selection for reverting
      nextTrialEligibleDate: null, // Calculated trial eligibility date
      isLoadingMembership: false, // Flag to skip trial validation during programmatic loads
      TrialOptions: [
        { text: "60 Day Free Trial", value: "01t37000001DyWg" },
        { text: "95 Day Free Trial", value: "01t37000001DyWq" },
      ],
      // TrailerOptions moved to computed property to use productCatalog
      TrailerSelection: "None",
      membership_number__c: "",
      isRenew: false,
      // Every membership on the loaded account. The one at
      // selectedMembershipIndex is bound to the form below and is what the
      // Submit button renews; the rest are edited in place in their panels.
      // The phone numbers the loaded contact arrived with, after normalizing.
      // A value still equal to one of these was not typed by the dealer.
      loadedPhones: { phone: null, mobilephone: null },
      // A search can return dozens of rows and an account can hold dozens of
      // memberships. Both used to render in full and push the form itself off
      // the screen, so both start trimmed.
      showAllResults: false,
      showAllMemberships: false,
      membershipContexts: [],
      selectedMembershipIndex: 0,
      // Contacts hang off the account, not the membership, so one list
      // serves every panel's picker.
      accountContacts: [],
      search_type: null,
      response_data: [],
      full_data: [],
      passed_in_acc_data: [],
      isBusy: false,
      tableFields: [
        {
          label: "Fill Data",
          key: "show_details",
          sortable: false,
        },

        {
          label: "Account Name",
          key: "account_name__c",
          sortable: false,
        },
        {
          label: "AOR",
          key: "aor_name__c",
          sortable: false,
        },
        {
          label: "AOR Description",
          key: "aor_description__c",
          sortable: false,
        },
        {
          label: "Memb. #",
          key: "membership_number__c",
          sortable: false,
        },
        {
          // A row is an account now, so flag when expanding it will surface
          // more than the one membership shown on the row.
          label: "Memberships",
          key: "membership_count",
          sortable: false,
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Phone #",
          key: "phone",
          sortable: false,
        },
        {
          label: "Cell #",
          key: "mobilephone",
          sortable: false,
        },
        {
          label: "Email",
          key: "email",
          sortable: false,
        },
        {
          label: "Status",
          key: "membership_status__c",
          sortable: false,
        },
        {
          label: "Exp. Date",
          key: "membership_expiration_date__c",
          sortable: false,
        },
        {
          label: "Auto Renew Status",
          key: "auto_renew__c",
          sortable: false,
        },
        {
          label: "Ongoing Promo Code",
          key: "renewal_promotion_code_name__c",
          sortable: false,
        },
        {
          key: "send_pass",
          label: `Send Digital Card 
      <b-icon 
        icon="info-circle-fill" 
        v-b-tooltip.hover 
        title="The Digital Card will be sent to the primary email on the contact in Salesforce"
      ></b-icon>`,
          tdClass: "text-center",
          thClass: "text-center",
          headerTitle: false,
        },
      ],
    };
  },
  validations: {
    additional_cc_list: {
      customEmail,
    },

    donation_amount: {},
    selected_trial_time_product: {
      required: requiredIf(function () {
        return this.CardSelection.includes("Trial");
      }),
    },
    referring_account: {
      required: requiredIf(function () {
        return this.CardSelection.includes("Trial");
      }),
    },
    campaign: {},
    contacts: {
      firstname: {
        required,
      },
      lastname: {
        required,
      },
      email: {
        required,
        // Shared with the membership panels so the two places a dealer types
        // an email agree on what a valid one is. Stricter than vuelidate's
        // `email`, which accepts a numeric top-level domain like "a@b.c1".
        email: isValidEmail,
      },
      // NANP content rules - no area code or exchange starting 0 or 1, no N11
      // service code - enforced on anything the dealer types, and waived for a
      // number the record arrived with. $v.$invalid gates the whole submission
      // including the charge (submitFormMixin), so a rule added here refuses a
      // payment rather than warning about a field. 1,791 contacts already in
      // Salesforce carry a number that breaks one of these; the member is not
      // responsible for that and their renewal should not stop over it. What
      // the dealer enters in this session is a different matter, and is held
      // to the rule. See isPhoneAcceptable.
      mobilephone: {
        integer,
        minlength: minLength(10),
        maxLength: maxLength(10),
        nanp(value) {
          return this.isPhoneAcceptable("mobilephone", value);
        },
      },
      phone: {
        required,
        integer,
        minlength: minLength(10),
        maxLength: maxLength(10),
        nanp(value) {
          return this.isPhoneAcceptable("phone", value);
        },
      },
    },
    account: {
      billingcity: {
        required: requiredIf(function () {
          return !this.CardSelection.includes("Trial");
        }),
      },
      billingcountry: {
        required: requiredIf(function () {
          return !this.CardSelection.includes("Trial");
        }),
      },
      billingpostalcode: {
        required: requiredIf(function () {
          return !this.CardSelection.includes("Trial");
        }),
      },
      billingstate: {
        required: requiredIf(function () {
          return !this.CardSelection.includes("Trial");
        }),
      },
      billingstreet: {
        required: requiredIf(function () {
          return !this.CardSelection.includes("Trial");
        }),
      },
      shippingcity: {
        required: requiredIf(function () {
          return !truthyBoolean(this.shipping_same_as_billing);
        }),
      },
      shippingcountry: {
        required: requiredIf(function () {
          return !truthyBoolean(this.shipping_same_as_billing);
        }),
      },
      shippingpostalcode: {
        required: requiredIf(function () {
          return !truthyBoolean(this.shipping_same_as_billing);
        }),
      },
      shippingstate: {
        required: requiredIf(function () {
          return !truthyBoolean(this.shipping_same_as_billing);
        }),
      },
      shippingstreet: {
        required: requiredIf(function () {
          return !truthyBoolean(this.shipping_same_as_billing);
        }),
      },
      name: {
        required,
      },
      acc_name_vald: {},
    },
    aor_display_field: {},
    boats: boatValidations,
    isStripeCardComplete: {
      // Validate that Stripe card is complete when credit card is required
      isComplete: function (value) {
        if (this.CreditCardRequired) {
          return value === true;
        }
        return true;
      },
    },
    membership_number__c: {},
    promotion_code: {},
  },
  created() {
    //Set up everything. Let's pull the account down from SFID
    if (this.$route.query.sfid != undefined) {
      getAllMembershipInfo(this.$route.query.sfid)
        .then((response) => {
          console.log(response);
          this.passed_in_acc_data = response.data;
          //Get Primary Membership
          var primary_membership_number = null;
          let primary_membership = undefined;
          this.passed_in_acc_data["memberships"].forEach(function (item) {
            if (item["primary_membership__c"]) {
              primary_membership = item;
              primary_membership_number = item["membership_number__c"];
            }
          });

          this.memno = primary_membership_number;
          //get primary contact
          let primary_contact = undefined;
          this.passed_in_acc_data["contacts"].forEach(function (item) {
            if (item["sfid"] == primary_membership["membership_contact__c"]) {
              primary_contact = item;
            }
          });

          //get primary boat
          let primary_boat = undefined;
          this.passed_in_acc_data["boats"].forEach(function (item) {
            if (item["primary_boat__c"]) {
              primary_boat = item;
            }
          });

          this.membership_expiration_date__c =
            primary_membership["membership_expiration_date__c"];

          this.membership_status__c =
            primary_membership["membership_status__c"];

          if (primary_membership["trailer_care_type__c"] != null) {
            if (primary_membership["trailer_care_type__c"] == "Trailer Care") {
              this.trailer_care_type__c = "Marine";
              this.TrailerSelection = "Marine";
            } else {
              this.trailer_care_type__c = "Universal";
              this.TrailerSelection = "Universal";
            }
          }

          //Set price so cart price populates properly
          this.updateCartPrice(this.TrailerSelection);
          this.updateCartPrice(this.CardSelection);

          var contact_parsed_obj = JSON.parse(JSON.stringify(this.contacts));
          var contact_keynames = Object.keys(contact_parsed_obj);
          var boat_parsed_obj = JSON.parse(JSON.stringify(this.boats));
          var boat_keynames = Object.keys(boat_parsed_obj);
          var account_parsed_obj = JSON.parse(JSON.stringify(this.account));
          var account_keynames = Object.keys(account_parsed_obj);

          var membership_parsed_obj = JSON.parse(
            JSON.stringify(this.memberships)
          );

          var membership_keynames = Object.keys(membership_parsed_obj);

          var arbs_parsed_obj = JSON.parse(JSON.stringify(this.arbs));
          var arbs_keynames = Object.keys(arbs_parsed_obj);

          this.contact_sfid = primary_contact["sfid"];
          this.routes.contact = this.contact_sfid;
          contact_keynames.forEach((name) => {
            primary_contact[name] = coerceContactField(
              name,
              primary_contact[name]
            );
            this.contacts[name] = primary_contact[name];
          });

          // Remember the numbers this record arrived with, so validation can
          // tell one the dealer typed from one the member has had on file for
          // years. populateRenewalForm in renewalMixin already did this; this
          // deep-link path (?sfid=) is a second, older copy of the same load
          // and was missing it, so a grandfathered number was treated as newly
          // typed and refused.
          this.rememberLoadedPhones();

          this.account_sfid = this.passed_in_acc_data["account"][0]["sfid"];
          this.routes.account = this.account_sfid;
          account_keynames.forEach((name) => {
            this.account[name] = coerceAccountField(
              name,
              this.passed_in_acc_data["account"][0][name]
            );
          });

          this.boat_sfid = primary_boat["sfid"];
          this.routes.boat = this.boat_sfid;
          boat_keynames.forEach((name) => {
            if (name == "aor__c") {
              this.old_aor = primary_boat[name];
            }
            this.boats[name] = coerceBoatField(name, primary_boat[name]);
          });

          this.membership_sfid = primary_membership["sfid"];
          this.routes.membership = this.membership_sfid;
          membership_keynames.forEach((name) => {
            if (
              name == "card_expiration_date__c" ||
              name == "card_number__c" ||
              name == "card_security_code__c" ||
              name == "card_type__c"
            ) {
              console.log("Skipping credit card field " + name);
            } else {
              this.memberships[name] = primary_membership[name];
            }
          });

          if (this.passed_in_acc_data["arbs"]["Error"] !== undefined) {
            if (
              this.passed_in_acc_data["arbs"]["Error"] ==
              "No pymt__payment_profile__c found with specified ID"
            ) {
              //no ARB found, nothing to do with it
              console.log("no arb found for user");
            } else {
              //ARB found, populate data so if they renew the member, they can cancel it
              this.arbs_sfid = this.passed_in_acc_data["arbs"][0]["sfid"];
              this.routes.arbs = this.arbs_sfid;
              arbs_keynames.forEach((name) => {
                //save all arb data in session
                this.arbs[name] = this.passed_in_acc_data["arbs"][0][name];
              });
            }
          } else {
            console.log("no arb found");
          }

          if (this.account.shippingstreet == this.account.billingstreet) {
            this.shipping_same_as_billing = true;
          } else {
            this.shipping_same_as_billing = false;
          }
          this.isRenew = true;
          this.$bvToast.toast(
            "The form has been populated with the member's information.",
            {
              title: "Data populated.",
              variant: "success",
              autoHideDelay: 2000,
            }
          );
        })
        .catch((error) =>
          this.handleGetAllInfoError(error, "created/url-sfid")
        );
    }

    if (this.$route.query.membershiptype != undefined) {
      // iterate over CardOptions and compare values
      this.CardOptions.forEach((option) => {
        if (this.$route.query.membershiptype.localeCompare(option.value) == 0) {
          this.CardSelection = option.value;
          this.updateCartPrice(this.CardSelection);
        }
      });
    }

    if (this.$route.query.promocode != undefined) {
      var promotion = this.$route.query.promocode;
      this.promotion_code = promotion;

      // Strip promocode from the URL after consuming it into promotion_code.
      // Pricing (calculateCartPrice/isFSI) used to read $route.query.promocode
      // directly, and the app never rewrites the URL, so a promocode left in
      // the address bar (e.g. FSI) silently zeroed every later renewal done in
      // the same tab. Keep any other query params (sfid, membershiptype).
      // eslint-disable-next-line no-unused-vars
      const { promocode, ...restQuery } = this.$route.query;
      this.$router
        .replace({ path: this.$route.path, query: restQuery })
        .catch(() => {});

      // this.$bvToast.toast(
      //   `We're searching for the promotion code ${promotion}`,
      //   {
      //     title: "Searching for promotions",
      //     variant: "info",
      //     autoHideDelay: 5000,
      //   }
      // );

      validatePromo(this.promoRequestPayload(promotion))
        .then((response) => {
          devLog("First response", response);
          if (response.data != null) {
            {
              //reject if associated with direct billing
              console.log(
                "checking if direct billing is true" +
                  response.data["associated_w_direct_billing__c"]
              );
              console.log(response.data["associated_w_direct_billing__c"]);
              if (
                response.data["associated_w_direct_billing__c"] == "true" ||
                response.data["associated_w_direct_billing__c"] == true
              ) {
                this.promotion_state_value = false;
                this.$bvToast.toast(
                  "Direct bill and subsidized promotion codes cannot be used in this app. Process directly inside of Salesforce.",
                  {
                    title: "Invalid promotion code.",
                    variant: "danger",
                    autoHideDelay: 5000,
                  }
                );
                this.updateCartPrice();
                return;
              }

              if (promotion?.toUpperCase() === "FSI") {
                this.funds_collected_locally = true;
              }

              this.promotion_valid_on_business_type__c =
                response.data["valid_on_business_type__c"];
              this.promotion_valid_on_membership__c =
                response.data["valid_on_membership__c"];
              this.promotion_state_value = true;
              this.promotion_sfid = response.data["sfid"];
              this.promotion_valid = true;
              this.promotion_value_in_days =
                response.data["value_time_in_days__c"];
              this.promotion_price =
                response.data["value_discount_in_dollars__c"];
              this.promotion_value_in_dollars =
                response.data["value_discount_in_dollars__c"];
              this.promotion_value_percentage_discount =
                response.data["value_discount_in_percentage__c"];
              this.promotion_type__c = response.data["promotion_type__c"];
              this.promotion_desc2 = response.data["description__c"];
              this.promotion_desc = response.data["promotion_details__c"];
              this.promotion_title = response.data["title__c"];
              this.promotion_ongoing_promotion_type =
                response.data["ongoing_promo_type__c"];

              // Validate promo code is applicable to the selected membership type
              if (!this.validatePromoMembershipType()) {
                this.resetPromotionDetails();
                return;
              }

              if (
                this.promotion_ongoing_promotion_type == "Ongoing with ARB" ||
                this.promotion_ongoing_promotion_type == "One Time with ARB"
              ) {
                console.log(
                  this.promotion_ongoing_promotion_type == "Ongoing with ARB" ||
                    this.promotion_ongoing_promotion_type == "One Time with ARB"
                );
                console.log("Setting autorenew_status and arb_value to true");
                this.autorenew_status = true;
                this.arb_value = true;
                this.promotion_arb_required = true;
              }

              this.updateCartPrice();
            }
          }
        })
        .catch((error) => {
          this.handlePromoApiError(error, promotion);
        });
    }
  },
  computed: {
    // Record type every contact ITFMA creates is written under. It decides
    // which Contact_Type__c values Salesforce will accept.
    visibleSearchResults() {
      if (this.showAllResults) return this.response_data;
      return this.response_data.slice(0, SEARCH_RESULT_PREVIEW);
    },
    hiddenSearchResultCount() {
      return Math.max(0, this.response_data.length - SEARCH_RESULT_PREVIEW);
    },
    // Each entry carries its own index in membershipContexts, because that
    // index is what selects a membership and binds it to the form - the
    // position within the trimmed list is not the same number.
    visibleMembershipPanels() {
      const all = this.membershipContexts.map((context, index) => ({
        context,
        index,
      }));
      if (this.showAllMemberships || all.length <= MEMBERSHIP_PREVIEW) {
        return all;
      }

      const shown = all.slice(0, MEMBERSHIP_PREVIEW);
      // The membership actually being renewed is never hidden, however far
      // down the list it sits - the form below is bound to it.
      if (!shown.some((item) => item.index === this.selectedMembershipIndex)) {
        const selected = all[this.selectedMembershipIndex];
        if (selected) shown.push(selected);
      }
      return shown;
    },
    hiddenMembershipCount() {
      return Math.max(
        0,
        this.membershipContexts.length - this.visibleMembershipPanels.length
      );
    },
    membershipsHeading() {
      const count = this.membershipContexts.length;
      return count === 1
        ? "Membership on this account"
        : `This account has ${count} memberships`;
    },
    membershipsHint() {
      return this.membershipContexts.length === 1
        ? "Contacts and boats can be corrected here without leaving the renewal."
        : "Select the one you are renewing. Others can be edited in place.";
    },
    contactRecordTypeId() {
      return RECORDTYPE_PERSON_CONTACT;
    },
    isDevelopmentMode() {
      return (process.env.VUE_APP_ENV || "dev") !== "production";
    },
    isFSI() {
      return this.promotion_code?.toUpperCase() === "FSI";
    },
    paymentAmount() {
      return this.isFSI ? this.card_price : this.price_total;
    },
    show_display_auto_renew() {
      if (
        this.promotion_ongoing_promotion_type === "Ongoing with ARB" ||
        this.promotion_ongoing_promotion_type == "One Time with ARB"
      ) {
        // turn the checkbox on if the promotion is ongoing with ARB
        return false;
      } else {
        return true;
      }
    },
    checkbox_display() {
      if (
        this.funds_collected_locally === true ||
        this.funds_collected_locally === "true" ||
        this.promotion_ongoing_promotion_type === "Ongoing with ARB" ||
        this.promotion_ongoing_promotion_type == "One Time with ARB"
      ) {
        return true;
      } else {
        return false;
      }
    },
    shouldUseSetupIntent() {
      // Use Setup Intent when we need to save payment method without charging today.
      // Scenarios:
      // 1. Funds are collected locally but auto-renew is enabled
      // 2. Price is $0 (e.g., activation code covers full amount) but credit card is required for ARB
      // 3. Enroll-only mode: existing member opting into ARB without taking payment today
      const isLocallyCollected =
        this.funds_collected_locally === true ||
        this.funds_collected_locally === "true";

      const isAutoRenew =
        this.arb_value === true ||
        this.arb_value === "true" ||
        this.autorenew_status === true ||
        this.autorenew_status === "true";

      const isZeroDollarWithCreditCardRequired =
        (this.price_total === 0 || this.price_total < 0.01) &&
        this.CreditCardRequired;

      return (
        (isLocallyCollected && isAutoRenew) ||
        isZeroDollarWithCreditCardRequired ||
        this.isArbEnrollOnly
      );
    },
    isArbEnrollOnly() {
      return (
        this.isRenew &&
        truthyBoolean(this.arb_value) &&
        truthyBoolean(this.arb_enroll_only) &&
        !this.isAlreadyEnrolledInArb
      );
    },
    // Amount actually charged today. When enrolling in auto-renew only, no
    // payment is taken today (the card is saved and billing is scheduled), so
    // "Total Today" must read $0 rather than the full membership price.
    totalDueToday() {
      if (this.isArbEnrollOnly) return 0;
      return this.price_total;
    },
    isAlreadyEnrolledInArb() {
      return this.isRenew && truthyBoolean(this.memberships.auto_renew__c);
    },
    nextRenewalDate() {
      // The membership's expiration date, which is when the next charge goes
      // out. This used to read active_arb_subscription_start_date - the date
      // the subscription BEGAN - and label it "Next renewal", so an active
      // member was always shown a date in the past. Richard Chafey's active
      // ARB started 2026-06-08 and the banner offered that as his next
      // renewal, three months after it had supposedly happened.
      //
      // Salesforce holds no next-charge field; the authoritative one is
      // Stripe's current_period_end. Until that is read directly, the
      // expiration date is the right answer for an annual membership that is
      // set to auto-renew, which is the only case this banner is shown for.
      const raw = this.membership_expiration_date__c;
      if (!raw) return null;
      return this.formatCalendarDate(raw);
    },

    cardOnFileDisplay() {
      const brand = this.memberships?.active_arb_card_type;
      const last4 = this.memberships?.active_arb_last_4_digits;
      if (!brand && !last4) return null;
      return [brand, last4 ? `ending in ${last4}` : null]
        .filter(Boolean)
        .join(" ");
    },
    getTrialOptions() {
      // Standalone selectors only — the TRIAL-EXIST ids this used to return for
      // active members are not valid selectors server-side, and CardOptions now
      // hides Trial for them anyway.
      return [
        { text: "60 Day Free Trial", value: "01t37000001DyWg" },
        { text: "95 Day Free Trial", value: "01t37000001DyWq" },
      ];
    },

    getTrialDays() {
      // Map trial product ID to number of days
      if (!this.selected_trial_time_product) {
        return 0;
      }

      const trialProductMap = {
        "01t37000001DyWg": 60, // 60 Day Free Trial
        "01t37000001DyWq": 95, // 95 Day Free Trial
        "01t1G000002c6cJQAQ": 60, // 60 Day Trial Active Membership
        "01t1G0000032ijgQAA": 95, // 95 Day Trial Active Membership
      };

      return trialProductMap[this.selected_trial_time_product] || 0;
    },

    goldCardPrice() {
      // Get the Gold Card price from product catalog
      // This is used for trial renewals to show the actual price they'll be charged
      return this.productCatalog?.membership?.gold?.price || 249.0;
    },

    recurringAnnualPrice() {
      // Calculate the recurring annual price, accounting for ongoing promotions
      // Ongoing promotions apply the discount every year, not just the first year
      let basePrice = (this.card_price || 0) + (this.trailering_price || 0);

      // Check if this is an ongoing promotion (discount applies to future renewals)
      const isOngoingPromotion =
        this.promotion_ongoing_promotion_type &&
        this.promotion_ongoing_promotion_type.includes("Ongoing");

      if (isOngoingPromotion && this.promotion_valid) {
        // Apply the ongoing discount to the recurring price
        if (
          this.promotion_type__c === "Dollar_Value_Promotion" ||
          this.promotion_type__c === "Dollar Value Promotion"
        ) {
          basePrice = basePrice - (this.promotion_value_in_dollars || 0);
        } else if (
          this.promotion_type__c === "Percentage Value Promotion" ||
          this.promotion_type__c === "Percentage_Value_Promotion"
        ) {
          basePrice =
            basePrice *
            (1 - (this.promotion_value_percentage_discount || 0) / 100);
        }
      }

      return Math.max(0, basePrice); // Ensure price doesn't go negative
    },

    consentObject() {
      // Generate consent object for payment recording
      // Records user consent for automatic renewal and membership agreement
      return {
        source: "ITFMA", // In The Field Membership App
        subscription:
          this.arb_value === true ||
          this.arb_value === "true" ||
          this.autorenew_status === true ||
          this.autorenew_status === "true",
        membership_agreement: true, // User agrees to terms by submitting payment
      };
    },

    // Human-readable payment summary shared by the confirmation email and the
    // success modal. Disambiguates the "funds collected locally" cases that
    // otherwise look like a hybrid credit-card / credit payment: it shows what
    // (if anything) was actually charged to a card today versus the invoice
    // total, and whether a card was saved only for a future auto-renewal.
    paymentSummaryLines() {
      const isLocal =
        this.funds_collected_locally === true ||
        this.funds_collected_locally === "true";
      const isAutoRenew =
        this.arb_value === true ||
        this.arb_value === "true" ||
        this.autorenew_status === true ||
        this.autorenew_status === "true";
      const isTrial = !!(
        this.CardSelection && this.CardSelection.includes("Trial")
      );
      const fmt = (n) => `$${(Math.round((n || 0) * 100) / 100).toFixed(2)}`;

      // Trials never collect a payment today. Show a dedicated, unambiguous
      // summary so staff aren't misled by the invoice/price defaults (the
      // price_total defaults to the full card price, which previously showed
      // as "$249 charged" on the confirmation modal for trials).
      if (isTrial) {
        const trialLines = [
          { label: "Membership Type", value: "Trial" },
          { label: "Payment Type", value: "Trial - no payment collected" },
          { label: "Amount Charged Today", value: fmt(0) },
          { label: "Auto-Renew", value: isAutoRenew ? "Enabled" : "Disabled" },
        ];
        if (this.stripeCardLast4) {
          trialLines.push({
            label: "Card on File (for auto-renew)",
            value: `${this.stripeCardBrand || "Card"} ending ${
              this.stripeCardLast4
            }`,
          });
        }
        return trialLines;
      }

      // Nothing is charged to a card today when funds are collected locally or
      // when we only save the card for a future auto-renewal (Setup Intent).
      const chargedToday =
        isLocal || this.shouldUseSetupIntent ? 0 : this.price_total;

      let paymentType;
      if (isLocal) {
        paymentType = "Credit (Funds collected locally - Cash/Check)";
      } else if (chargedToday > 0 || this.stripeCardLast4) {
        paymentType = "Credit Card (Stripe)";
      } else {
        paymentType = "No payment taken";
      }

      const lines = [
        { label: "Funds Collected Locally", value: isLocal ? "Yes" : "No" },
        { label: "Payment Type", value: paymentType },
        { label: "Amount Charged Today", value: fmt(chargedToday) },
        { label: "Invoice Total", value: fmt(this.price_total) },
        { label: "Auto-Renew", value: isAutoRenew ? "Enabled" : "Disabled" },
      ];

      if (this.stripeCardLast4) {
        lines.push({
          label: isLocal ? "Card on File (for auto-renew)" : "Card",
          value: `${this.stripeCardBrand || "Card"} ending ${
            this.stripeCardLast4
          }`,
        });
      }

      return lines;
    },
    paymentSummaryText() {
      const rows = this.paymentSummaryLines
        .map((line) => `${line.label}: ${line.value}`)
        .join("\n");
      return `\n\n----- PAYMENT SUMMARY -----\n${rows}`;
    },

    getEmailBody() {
      if (!this.CreditCardRequired) {
        //use lead body
        var body = `Form Submitted By: ${this.cachedName}\nName: ${
          this.account.name
        }\nEmail: ${this.contacts.email}\nPhone Number: ${
          this.contacts.phone
        }\nAddress: ${this.account.shippingstreet} ${
          this.account.shippingcity
        } ${this.account.shippingstate} ${
          this.account.shippingpostalcode
        }\nMembership Type: ${this.CardSelection}\nTrailering Type: ${
          this.TrailerSelection
        }\nTotal price: $${
          this.price_total != null
            ? (Math.round(this.price_total * 100) / 100).toFixed(2)
            : "0.00"
        }\nPromotion Type: ${this.promotion_code}`;
        return body + this.paymentSummaryText;
      } else if (this.isRenew) {
        //use renew body
        var body2 = `Form Submitted By: ${this.cachedName}\nName: ${
          this.account.name
        }\nMembership Number: ${this.memno}\nEmail: ${
          this.contacts.email
        }\nPhone Number: ${this.contacts.phone}\nAddress: ${
          this.account.shippingstreet
        } ${this.account.shippingcity} ${this.account.shippingstate} ${
          this.account.shippingpostalcode
        }\nMembership Type: ${this.CardSelection}\nTrailering Type: ${
          this.TrailerSelection
        }\nPromotion Type: ${this.promotion_code}\nTotal price: $${
          this.price_total != null
            ? (Math.round(this.price_total * 100) / 100).toFixed(2)
            : "0.00"
        }`;
        return body2 + this.paymentSummaryText;
      } else {
        // use new member body
        var body3 = `Form Submitted By: ${this.getName}\nName: ${
          this.account.name
        }\nEmail: ${this.contacts.email}\nPhone Number: ${
          this.contacts.phone
        }\nAddress: ${this.account.shippingstreet} ${
          this.account.shippingcity
        } ${this.account.shippingstate} ${
          this.account.shippingpostalcode
        }\nMembership Type: ${this.CardSelection}\nTrailering Type: ${
          this.TrailerSelection
        }\nPromotion Type: ${this.promotion_code}\nTotal price: $${
          this.price_total != null
            ? (Math.round(this.price_total * 100) / 100).toFixed(2)
            : "0.00"
        }`;
        return body3 + this.paymentSummaryText;
      }
    },
    getEmailList() {
      var list_name = [];

      var tempName = this.cachedName;
      if (tempName != null) {
        list_name.push(tempName);
      } else {
        list_name.push("pgillan@seatow.com");
      }

      if (
        !this.$v.additional_cc_list.$invalid &&
        this.additional_cc_list != ""
      ) {
        var otherArr = this.$v.additional_cc_list.$model.split(","); //break apart the additional CC list and combine it to list_name
        list_name = list_name.concat(otherArr);
      }

      if (
        (process.env.VUE_APP_ENV || "dev") === "production" &&
        this.aor_contact_email != null
      ) {
        list_name.push(this.aor_contact_email);
      } else {
        console.log(
          "NOT SENDING BECAUSE NOT PROD ENVIORNMENT, BUT AOR CONTACT FOUND: ",
          this.aor_contact_email
        );
      }

      return [...new Set(list_name)];
    },
    getName() {
      if (this.cachedName !== undefined || this.cachedName !== null) {
        return this.cachedName;
      }
      console.log("this.user", this.user);
      if (auth.user) {
        return auth.user.name;
      } else {
        return "Invalid User";
      }
    },
    CreditCardRequired() {
      const isArbRequired =
        this.promotion_arb_required === true ||
        this.promotion_arb_required === "true" ||
        this.activation_codes?.usage_rules__c?.includes("ARB Required");

      const isAutoRenew =
        this.autorenew_status === "true" || this.autorenew_status === true;

      const isLocallyCollected =
        this.funds_collected_locally === "true" ||
        this.funds_collected_locally === true;

      // When activation code covers full cost AND auto-renew is enabled,
      // we need to collect card for future charges (use SetupIntent)
      const isZeroDollarWithAutoRenew =
        (this.price_total === 0 || this.price_total < 0.01) &&
        this.activation_codes?.value__c > 0 &&
        isAutoRenew;

      console.log(
        `creditCardRequiredisArbRequired: ${isArbRequired}, isAutoRenew: ${isAutoRenew}, isLocallyCollected: ${isLocallyCollected}, isZeroDollarWithAutoRenew: ${isZeroDollarWithAutoRenew}`
      );

      return (
        isArbRequired ||
        ((this.price_total > 0 || isAutoRenew) && !isLocallyCollected) ||
        (isAutoRenew && isLocallyCollected) ||
        isZeroDollarWithAutoRenew
      );
    },
    payButtonDisabledReason() {
      // Returns a message explaining why the Pay button is disabled, or null if it's not disabled
      if (!this.productsLoaded) {
        return "Loading product information...";
      }

      const missingFields = [];
      if (!this.contacts.email) {
        missingFields.push("email");
      } else if (this.$v.contacts.email && !this.$v.contacts.email.email) {
        return "Please enter a valid email address (include @ and domain)";
      }

      if (!this.contacts.firstname) missingFields.push("first name");
      if (!this.contacts.lastname) missingFields.push("last name");

      if (missingFields.length > 0) {
        return `Please enter customer ${missingFields.join(", ")} to continue`;
      }

      return null; // Button is not disabled
    },
    marina_desc() {
      if (this.boats.home_port_type__c == null) {
        return "Please select where your boat is kept above.";
      }
      var marina_dict = {};
      marina_dict["Marina"] = "Name of marina where boat is kept:";
      marina_dict["Trailer"] = "Boat ramp most frequently used:";
      marina_dict["Marina"] = "Name of marina where boat will be kept:";
      marina_dict["Trailer"] =
        "Boat Ramp most likely to use majority of the time:";
      marina_dict["Mooring"] = "Body of water / Mooring location:";
      marina_dict["Home Dock"] = "Address of home dock:";
      marina_dict["Transient"] = "Please describe in more detail:";
      marina_dict["Other"] = "Please describe in more detail:";

      return marina_dict[this.boats.home_port_type__c];
    },
    authnet_expiration() {
      return `${this.card_expiration_month}/${this.card_expiration_year}`;
    },
    tooltip_display_text() {
      if (
        this.show_display_auto_renew === true ||
        this.show_display_auto_renew === "true"
      ) {
        return "If the funds were collected locally, no need to enter payment information unless auto-renew is selected. We insert into salesforce with a credit.";
      } else {
        return "If the funds are collected locally and the member wants automatic renewal you must set up the AR subscription directly in Salesforce or instruct the member to set up AR via their membership dashboard on seatow.com.";
      }
    },
    current_year_add_one() {
      return new Date().getFullYear() + 1;
    },
    promotionstate() {
      return this.promotion_state_value;
    },
    promotionFeedback() {
      if (!this.promotion_valid) {
        return "This promotion code is not valid.";
      }

      return "Enter a valid promotion code, if applicable.";
    },
    campaignstate() {
      return this.campaign_state_value;
    },
    campaignFeedback() {
      if (!this.campaign_valid) {
        return "This campaign code is not valid.";
      }

      return "Enter a valid promotion code, if applicable.";
    },
    CardOptions() {
      // Use productCatalog if loaded, otherwise return empty array until loaded
      if (!this.productsLoaded || !this.productCatalog) {
        return [
          {
            text: "Loading...",
            value: "Gold",
            cost: 0,
            disabled: true,
            title: "Loading product information...",
          },
        ];
      }

      const goldPrice = this.productCatalog.membership?.gold?.price || 249.0;
      const lakePrice = this.productCatalog.membership?.lake?.price || 169.0;
      const commercialPrice =
        this.productCatalog.membership?.commercial?.price || 599.0;
      const profmarinerPrice =
        this.productCatalog.membership?.profmariner?.price || 499.0;

      const options = [
        {
          text: `Gold Card - $${goldPrice.toFixed(2)}`,
          value: "Gold",
          cost: goldPrice,
          title:
            "The choice of over 95% of Sea Tow members. This card provides membership benefits for any recreational vessel that has an engine and is registered to or owned by the member (covered vessels). Any person operating a covered vessel is entitled to receive membership benefits for that vessel. The Gold Card member may also use his/her privileges on any vessel he/she charters, rents, leases or borrows. For complete details on all Gold Card member privileges please see our Membership Agreement.",
        },
        {
          text: `Lake Card - $${lakePrice.toFixed(2)}`,
          value: "Lake",
          cost: lakePrice,
          disabled: this.$data.isHomeportFlorida,
          title:
            "If you boat on fresh, non-tidal, inland waterways (excluding Florida) then the Lake Card may be the perfect option for you. The Sea Tow Lake Card provides the same member benefits as the Gold Card, but on ALL fresh, non-tidal, inland waterways. For complete details on all Lake Card member privileges please see our Membership Agreement.",
        },
        {
          text: `Commercial Card - $${commercialPrice.toFixed(2)}`,
          value: "Commercial",
          cost: commercialPrice,
          title:
            "This card provides service for commercial vessels. The Commercial Card covers the primary vessel only. Any person operating the primary vessel is entitled to receive all membership benefits for that vessel.",
        },
        {
          text: `Prof. Mariner Card - $${profmarinerPrice.toFixed(2)}`,
          value: "ProfMariner",
          cost: profmarinerPrice,
          title:
            "If you make your living on the water, this card is for you. The Professional Mariner Card is a service package for individuals who regularly use multiple vessels in the performance of their maritime duties such as: yacht delivery captains, on-water instructors, etc. Any vessel the member is operating and is the master of, is entitled to receive membership benefits for that vessel, except Dock-to-Dock Tows.",
        },
      ];

      // Salesforce rejects a standalone trial for an active member; those days
      // go on a renewal purchase instead.
      if (this.membership_status__c !== "Active") {
        options.push({
          text: "Trial - $0.00",
          value: "TrialCard",
          cost: 0.0,
          title: "Trial card",
        });
      }

      return options;
    },
    TrailerOptions() {
      // Use productCatalog if loaded, otherwise return default options
      if (!this.productsLoaded || !this.productCatalog) {
        return [
          { text: "Loading...", value: "None", cost: 0.0, disabled: true },
        ];
      }

      const marinePrice =
        this.productCatalog.trailer_care?.marine?.price || 16.0;
      const universalPrice =
        this.productCatalog.trailer_care?.universal?.price || 30.0;

      return [
        {
          text: `Trailer Care - $${marinePrice.toFixed(2)}`,
          value: "Marine",
          cost: marinePrice,
        },
        {
          text: `Trailer Care Plus - $${universalPrice.toFixed(2)}`,
          value: "Universal",
          cost: universalPrice,
        },
        { text: "No Roadside Assistance", value: "None", cost: 0.0 },
      ];
    },
  },
  components: {
    VueQrReader,
    Dropdown,
    ActivationCodeInput,
    PromoCodeDiscrepancyModal,
    StripePaymentForm,
    BoatInfoCard,
    ContactCard,
    AddressCard,
    TrialCard,
    MembershipPanel,
  },
  mixins: [
    submissionMixin,
    submitFormMixin,
    renewalMixin,
    catalogMixin,
    promoCampaignMixin,
    uxcamMixin,
  ],
  methods: {
    // Pure helpers (no component state) live in ./dealerForm/helpers.js and are
    // mixed in here so template bindings and this.X() call sites are unchanged.
    ...pureHelperMethods,
    async initializeStripePayment() {
      console.log("💳 Pay button clicked!");
      console.log(
        "🔍 DealerForm existingStripeCustomerId:",
        this.existingStripeCustomerId
      );
      console.log("🔍 DealerForm contacts.email:", this.contacts.email);
      console.log(
        "🔍 DealerForm contacts name:",
        `${this.contacts.firstname} ${this.contacts.lastname}`
      );

      // Mark as initialized so the Pay button is replaced with the form
      this.stripePaymentInitialized = true;

      // Wait for next tick to ensure component is rendered
      await this.$nextTick();

      console.log(
        "🔍 After nextTick, StripePaymentForm component exists:",
        !!this.$refs.stripePaymentForm
      );
      if (this.$refs.stripePaymentForm) {
        console.log(
          "🔍 StripePaymentForm.existingCustomerId prop:",
          this.$refs.stripePaymentForm.existingCustomerId
        );
      }

      // Call the StripePaymentForm's initializeStripe method
      if (this.$refs.stripePaymentForm) {
        await this.$refs.stripePaymentForm.initializeStripe();
      }
    },
    clearStripePaymentData() {
      // Clear Stripe payment result data (set after successful payment)
      this.transId = null;
      this.stripePaymentIntentId = null;
      this.pendingOppGuid = null;
      this.stripeCustomerId = null;
      this.stripePaymentMethodId = null;
      this.stripeCardBrand = null;
      this.stripeCardLast4 = null;
      this.stripeExpMonth = null;
      this.stripeExpYear = null;

      // Clear existing customer data (set when loading member from search)
      this.existingStripeCustomerId = null;
      this.existingStripeCustomerSfid = null;

      // Reset payment initialization flag
      this.stripePaymentInitialized = false;

      console.log("✅ Cleared all Stripe payment data");
    },
    fillTestData() {
      // Clear ALL Stripe data since this is a new test member
      this.clearStripePaymentData();

      // Personal Information (using Vuelidate $model)
      this.$v.contacts.firstname.$model = "Test";
      this.$v.contacts.lastname.$model = "User";
      this.$v.contacts.email.$model = `test${Date.now()}@seatow.com`;
      this.$v.contacts.phone.$model = "6315550123";
      this.$v.contacts.mobilephone.$model = "6315550124";

      // Account Information (using Vuelidate $model)
      this.$v.account.name.$model = "Test User";
      this.$v.account.billingstreet.$model = "123 Test Harbor Lane";
      this.$v.account.billingcity.$model = "Southold";
      this.$v.account.billingstate.$model = "NY"; // Use state abbreviation
      this.$v.account.billingpostalcode.$model = "11971";
      this.$v.account.billingcountry.$model = "United States";

      // Same as billing for shipping
      this.$v.account.shippingstreet.$model =
        this.$v.account.billingstreet.$model;
      this.$v.account.shippingcity.$model = this.$v.account.billingcity.$model;
      this.$v.account.shippingstate.$model = "NY"; // Use state abbreviation
      this.$v.account.shippingpostalcode.$model =
        this.$v.account.billingpostalcode.$model;
      this.$v.account.shippingcountry.$model =
        this.$v.account.billingcountry.$model;

      // Boat Information
      // Fields with Vuelidate validation - use $model
      this.$v.boats.name__c.$model = "Test Boat";
      this.$v.boats.boat_make__c.$model = "Test Marine";
      this.$v.boats.year__c.$model = new Date().getFullYear();
      this.$v.boats.length__c.$model = 25;
      this.$v.boats.color__c.$model = "White";
      this.$v.boats.engine_type__c.$model = "Outboard";
      this.$v.boats.marina_name__c.$model = "Port of Egypt"; // Name of marina
      this.$v.boats.home_port_type__c.$model = "Marina"; // Boat is kept at: Marina
      this.$v.boats.home_port_city__c.$model = "Southold"; // Boat City
      this.$v.boats.home_port_state__c.$model = "New York"; // Boat State (uses full name)
      this.$v.boats.home_port_country__c.$model = "United States";

      // Fields without Vuelidate validation - set directly
      this.boats.fuel_type__c = "Gasoline";
      this.boats.number_of_engines__c = 1;
      this.boats.primary_boat__c = true;

      // Membership Type - set flag to skip trial validation
      this.isLoadingMembership = true;
      this.CardSelection = "Gold";
      this.TrailerSelection = "None";
      this.trailering_price = 0;
      this.$nextTick(() => {
        this.isLoadingMembership = false;
      });

      // Auto-Renew (IMPORTANT for testing subscriptions)
      this.arb_value = true;

      // Update cart price
      this.updateCartPrice(this.CardSelection);

      // Mark all fields as touched so validation shows as valid
      this.$v.$touch();

      // Trigger AOR (Area of Responsibility) lookup based on boat location
      this.$nextTick(() => {
        this.assignAOR();
      });

      console.log("✅ Test data filled successfully!");
      console.log("📧 Email:", this.$v.contacts.email.$model);
      console.log("🔄 Auto-renew enabled:", this.arb_value);
      console.log("⚓ Boat port type:", this.$v.boats.home_port_type__c.$model);
      console.log("🏢 Marina name:", this.$v.boats.marina_name__c.$model);
      console.log("🏙️  Boat city:", this.$v.boats.home_port_city__c.$model);
      console.log("📍 Boat state:", this.$v.boats.home_port_state__c.$model);
    },

    // Shared handler for failures that occur AFTER the card has already been
    // charged in Stripe but while creating the Salesforce records. Without
    // this, a rejected post (e.g. a 500 from /memberships/) silently aborted
    // the chain: no success message, partial/no SF records, card still charged.
    handlePostChargeSfFailure(stage, error) {
      const intentRef = this.stripePaymentIntentId || this.transId || "unknown";
      console.error(
        `Salesforce record creation failed at stage "${stage}" after Stripe charge. ` +
          `Stripe payment intent: ${intentRef}.`,
        error?.response?.data || error?.message || error
      );
      this.$bvToast.toast(
        `The payment was charged in Stripe but creating the Salesforce records failed (step: ${stage}). ` +
          `DO NOT re-charge the member. Please contact the helpdesk with this Stripe reference: ${intentRef}.`,
        {
          title: "Payment captured, but record creation failed",
          variant: "danger",
          noAutoHide: true,
        }
      );
      this.disableSubmit = false;
    },

    useRenewalPromoCode() {
      this.promotion_code = this.renewalPromoCode;
      this.submitPromo(this.renewalPromoCode);
      this.showPromoCodeModal = false;
    },
    useFormPromoCode() {
      this.submitPromo(this.tempFormPromoCode);
      this.showPromoCodeModal = false;
    },
    onSendPassClick(row) {
      let primary = null;

      getAllMembershipInfo(row.account__c)
        .then((response) => {
          console.log("response", response);

          if (response.data.memberships.length > 0) {
            console.log(
              "Membership length is: ",
              response.data.memberships.length
            );
            // get the primary from the reponse
            response.data.memberships.forEach((membership) => {
              if (membership.primary_membership__c === true) {
                console.log("Primary membership found");
                primary = membership;
                return;
              } else {
                console.log("not primary");
              }
            });
          } else if (response.data.memberships.length === 1) {
            console.log("Only one membership");
            primary = response.data.memberships[0];
          } else {
            console.log(response);
            console.log("No primary membership found");
            return;
          }

          console.log("primary is set to: ", primary);
          if (primary === null || primary === undefined) {
            console.log("No primary membership found (null or primary)");
            return;
          }

          // send request
          resendCard(primary.sfid)
            .then((response) => {
              console.log(response);
              this.$bvToast.toast("Digital Card sent successfully", {
                title: "Success",
                variant: "success",
              });
            })
            .catch((error) => {
              console.log(error);
              this.$bvToast.toast("Failed to send Digital Card", {
                title: "Error",
                variant: "danger",
              });
            });
        })
        .catch((error) =>
          this.handleGetAllInfoError(error, "resend-digital-card")
        );
    },
    async ensureUser() {
      if (!auth.user) {
        const token = await auth.acquireToken();
        auth.user = await auth.getUserInfo(token);
      }
      return auth.user;
    },
    // Cancelling is now done from a membership's own panel, so the handler is
    // told WHICH membership. Clearing the form's flags unconditionally would
    // have shown the renewal in progress as no-longer-auto-renewing because a
    // different membership on the same account was cancelled.
    onArbCancelled(context) {
      if (context) {
        context.membership.auto_renew__c = false;
      }

      const selected = this.membershipContexts[this.selectedMembershipIndex];
      if (!context || context === selected) {
        this.memberships.auto_renew__c = false;
        this.arb_value = false;
        this.autorenew_status = false;
      }
    },
    async enrollExistingMemberInArb() {
      if (!this.membership_sfid || !this.account_sfid || !this.contact_sfid) {
        this.$bvToast.toast("Load a member before enrolling in auto-renew.", {
          title: "Member not loaded",
          variant: "warning",
          autoHideDelay: 5000,
        });
        return;
      }

      const expiration = this.membership_expiration_date__c;
      if (!expiration) {
        this.$bvToast.toast(
          "This member has no membership expiration date on file. Cannot schedule auto-renew.",
          {
            title: "Missing expiration date",
            variant: "danger",
            autoHideDelay: 6000,
          }
        );
        return;
      }

      if (!this.$refs.stripePaymentForm) {
        this.$bvToast.toast(
          'Click "Set Up Subscription" and enter the card before submitting.',
          {
            title: "Payment form not ready",
            variant: "warning",
            autoHideDelay: 6000,
          }
        );
        return;
      }

      this.disableSubmit = true;

      try {
        const setupResult = await this.$refs.stripePaymentForm.processPayment();
        if (!setupResult || !setupResult.success) {
          this.$bvToast.toast(setupResult?.error || "Failed to save card.", {
            title: "Setup failed",
            variant: "danger",
            autoHideDelay: 6000,
          });
          return;
        }

        const customerId =
          typeof setupResult.customerId === "object"
            ? setupResult.customerId.id
            : setupResult.customerId;

        this.stripeCustomerId = customerId;
        this.stripePaymentMethodId = setupResult.paymentMethodId;
        this.stripeCardBrand = setupResult.cardBrand || "Unknown";
        this.stripeCardLast4 = setupResult.cardLast4 || "";
        this.stripeExpMonth = setupResult.expMonth;
        this.stripeExpYear = setupResult.expYear;

        const isUpdate = this.existingStripeCustomerSfid != null;
        const stripeResponse = await insertStripeCustomerPaymentMethod({
          account_sfid: this.account_sfid,
          contact_sfid: this.contact_sfid,
          stripe_customer_id: customerId,
          stripe_payment_method_id: setupResult.paymentMethodId,
          card_type: this.stripeCardBrand,
          last_4_digits: this.stripeCardLast4,
          expiration_month: this.stripeExpMonth,
          expiration_year: this.stripeExpYear,
          is_renewal: true,
          is_update: isUpdate,
          existing_customer_sfid: this.existingStripeCustomerSfid,
        });

        const paymentMethodHerokuId =
          stripeResponse?.data?.payment_method_heroku_id;

        await manageSubscriptionSchedule({
          account_sfid: this.account_sfid,
          contact_sfid: this.contact_sfid,
          membership_sfid: this.membership_sfid,
          customer_id: customerId,
          payment_method_id: setupResult.paymentMethodId,
          payment_method_heroku_id: paymentMethodHerokuId,
          auto_renew: true,
          is_renewal: false,
          membership_type: this.CardSelection,
          current_expiration_date: expiration,
          bonus_days: 0,
        });

        await patchMembership({
          sfid: this.membership_sfid,
          auto_renew__c: true,
        });

        if (this.$refs.stripePaymentForm) {
          this.$refs.stripePaymentForm.clientSecret = null;
          this.$refs.stripePaymentForm.setupIntentId = null;
        }

        this.$bvToast.toast(
          "The member has been enrolled in auto-renew. No payment was taken.",
          {
            title: "Enrolled in auto-renew",
            variant: "success",
            autoHideDelay: 6000,
          }
        );
      } catch (error) {
        devError("enrollExistingMemberInArb failed:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong while enrolling in auto-renew.";
        this.$bvToast.toast(message, {
          title: "Auto-renew enrollment failed",
          variant: "danger",
          autoHideDelay: 8000,
        });
      } finally {
        this.disableSubmit = false;
      }
    },
    validateUsageRules() {
      if (!this.activation_codes.usage_rules__c) return true;

      if (
        this.activation_codes.usage_rules__c === "ARB Required" &&
        !this.autorenew_status
      ) {
        this.showErrorToast(
          "This activation code requires auto-renew to be enabled. Please enable auto-renew to use this code.",
          "Auto-Renew Required"
        );
        this.$refs.activationCodeInput.setInvalid();
        return false;
      }
      return true;
    },
    validateBusinessType() {
      if (!this.activation_codes.valid_on_business_type__c) return true;

      // Salesforce multi-select picklists are semicolon-delimited
      // (e.g. "New;Renew;Reacquired"). Tolerate commas too, and trim spaces.
      const valid_on_business_types =
        this.activation_codes.valid_on_business_type__c
          .split(/[;,]/)
          .map((type) => type.trim())
          .filter(Boolean);

      // Classify the current transaction's business type: an expired member
      // renewing is "Reacquired", any other renewal is "Renew", else "New".
      const isExpiredRenewal =
        this.isRenew && this.membership_status__c === "Expired";
      const matchesBusinessType =
        (valid_on_business_types.includes("New") && !this.isRenew) ||
        (valid_on_business_types.includes("Renew") && this.isRenew) ||
        (valid_on_business_types.includes("Reacquired") && isExpiredRenewal);

      if (matchesBusinessType) {
        this.$refs.activationCodeInput.setValid();
        return true;
      }

      this.showErrorToast(
        "This activation code is only valid for [" +
          valid_on_business_types.join(", ") +
          "] memberships.",
        "Invalid Activation Code for Membership Process"
      );
      this.$refs.activationCodeInput.setInvalid();
      return false;
    },
    validateMembershipType() {
      if (!this.activation_codes.valid_on_membership__c) return true;

      // Semicolon-delimited Salesforce multi-select (tolerate commas too).
      const valid_membership_types =
        this.activation_codes.valid_on_membership__c
          .split(/[;,]/)
          .map((type) => type.trim())
          .filter(Boolean);
      if (
        valid_membership_types.some((type) => type.includes(this.CardSelection))
      ) {
        this.$refs.activationCodeInput.setValid();
        return true;
      }

      this.showErrorToast(
        "This activation code is not valid for the selected membership type.",
        "Invalid Activation Code"
      );
      this.$refs.activationCodeInput.setInvalid();
      return false;
    },
    handleGetAllInfoError(error, context) {
      devError(`getallinfo failed (${context}):`, error);
      // A half-loaded member leaves ids set with isRenew false — the shape that
      // makes a later submit overwrite whoever was loaded.
      this.clearRecordIdentity();
      this.$bvToast.toast(
        "We couldn't load this member's data. Please try again, and contact support if it keeps happening.",
        {
          title: "Failed to load member data",
          variant: "danger",
          autoHideDelay: 8000,
        }
      );
    },
    promoRequestPayload(promotion) {
      const payload = { promotion_code: promotion };

      const cardTypeMap = {
        Gold: "Gold",
        Lake: "Lake",
        Commercial: "Commercial",
        ProfMariner: "Professional Mariner",
      };
      const apiCardType = cardTypeMap[this.CardSelection];
      if (apiCardType) {
        payload.selected_card_type = apiCardType;
      }

      payload.flow_type = this.isRenew ? "Renew" : "New";

      return payload;
    },
    handlePromoApiError(error, promotion) {
      this.promotion_state_value = false;

      const data = error?.response?.data || {};
      const errorCode = data?.errors?._error;
      const message = data?.message;

      if (errorCode === "INVALID_ID") {
        this.$bvToast.toast(`${promotion} was not found.`, {
          title: "Promotion code not found",
          variant: "warning",
          autoHideDelay: 5000,
        });
        return;
      }

      if (
        errorCode === "INVALID_CARD_TYPE" ||
        errorCode === "INVALID_FLOW_TYPE"
      ) {
        this.$bvToast.toast(message, {
          title: "Invalid promotion code.",
          variant: "danger",
          autoHideDelay: 8000,
        });
        return;
      }

      if (errorCode === "INACTIVE_PROMO") {
        this.$bvToast.toast(message || "This promotion is no longer active.", {
          title: "Invalid promotion code.",
          variant: "danger",
          autoHideDelay: 5000,
        });
        return;
      }

      if (errorCode === "NO_ADDITIONAL_TRIAL_TIME_PROMOTION") {
        this.$bvToast.toast(
          message ||
            "Additional Trial Time promotions are not valid to be used here.",
          {
            title: "Invalid promotion code.",
            variant: "danger",
            autoHideDelay: 5000,
          }
        );
        return;
      }

      this.$bvToast.toast(
        message ||
          "This promotion code is either no longer active, or not applicable to the current membership.",
        {
          title: "Invalid promotion code.",
          variant: "danger",
          autoHideDelay: 5000,
        }
      );
    },
    validatePromoMembershipType() {
      if (
        !this.promotion_valid_on_membership__c ||
        this.promotion_valid_on_membership__c.toLowerCase() === "all"
      ) {
        return true;
      }

      const cardTypeMap = {
        Gold: "Gold",
        Lake: "Lake",
        Commercial: "Commercial",
        ProfMariner: "Professional Mariner",
      };
      const selectedCardType =
        cardTypeMap[this.CardSelection] || this.CardSelection;

      const validTypes = this.promotion_valid_on_membership__c.split(";");
      if (
        validTypes.some((type) =>
          type.trim().toLowerCase().includes(selectedCardType.toLowerCase())
        )
      ) {
        return true;
      }

      this.promotion_state_value = false;
      this.$bvToast.toast(
        `This promotion code is only valid for: ${
          this.promotion_valid_on_membership__c
        }. It cannot be applied to the selected ${this.readableCardName(
          this.CardSelection
        )} Card.`,
        {
          title: "Invalid promotion code for selected membership.",
          variant: "danger",
          autoHideDelay: 8000,
        }
      );
      return false;
    },
    showErrorToast(message, title) {
      this.$bvToast.toast(message, {
        title: title,
        variant: "danger",
        solid: true,
      });
    },
    handleActivationCode(code) {
      if (
        (code === undefined || code === null) &&
        this.activation_codes.name === null
      ) {
        return;
      }
      validateActivationCode(code)
        .then((response) => {
          this.activation_codes = response.data;

          if (this.activation_codes.card_status__c === "Used") {
            this.showErrorToast(
              "This activation code has already been used.",
              "Invalid Activation Code"
            );
            this.$refs.activationCodeInput.setInvalid();
            return;
          }

          const isValid =
            this.validateUsageRules() &&
            this.validateBusinessType() &&
            this.validateMembershipType() &&
            this.activation_codes.card_status__c === "Placed";

          if (isValid) {
            this.$refs.activationCodeInput.setValid();
            this.calculateCartPrice();
          } else {
            console.log("Invalid activation code");
            this.$refs.activationCodeInput.setInvalid();
            return;
          }
        })
        .catch((error) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Invalid activation code";
          this.showErrorToast(errorMessage, "Error");
          this.$refs.activationCodeInput.setInvalid();
        });
    },
    resetActivationCodes() {
      this.$refs.activationCodeInput.reset();
      if (this.activation_codes) {
        Object.keys(this.activation_codes).forEach((key) => {
          console.log("Setting " + key + " to null");
          this.activation_codes[key] = null;
        });
      }

      this.$bvToast.toast("Activation code has been cleared from the form", {
        title: "Activation Code Cleared",
        variant: "info",
        solid: true,
      });

      this.calculateCartPrice();
    },
    async getAORContactEmail() {
      console.log("aor_display_field", this.aor_display_field);
      if (
        this.aor_display_field === null ||
        this.aor_display_field === undefined ||
        this.aor_display_field === "999 (International)"
      ) {
        console.log("aor_display_field is null || default");
        return;
      }
      const aorSfid = this.aor_display_field.match(/\((a0d[^)]+)\)/)[1];
      console.log("attempt to get aor owner email");
      try {
        const response = await getAorContactEmailBySfid(aorSfid);
        if (response.data && response.data.aor_contact_email) {
          console.log(
            "aor contact email found: ",
            response.data.aor_contact_email
          );
          this.aor_contact_email = response.data.aor_contact_email;
        } else {
          this.aor_contact_email = null;
        }
      } catch (error) {
        this.aor_contact_email = null;
        console.log("Error fetching AOR contact email:", error);
      }
    },
    // Add this method to your component's methods
    checkAORMatch() {
      //TEST
      console.log("aor_display_field", this.aor_display_field);
      //get the text starting with 'a0d' from aor_display_field
      var new_aor = this.aor_display_field.match(/\((a0d[^)]+)\)/)[1];

      console.log("this.old_aor", this.old_aor);
      console.log("this.new_aor", new_aor);

      if (this.old_aor === null) {
        return true;
      }

      return this.old_aor === new_aor;
    },
    handlePromotionCode(primary_membership) {
      if (
        this.promotion_code != null &&
        this.promotion_code != undefined &&
        this.promotion_code != ""
      ) {
        // Store the form's promotion code temporarily
        this.tempFormPromoCode = this.promotion_code;

        // Get the renewal promotion code
        this.renewalPromoCode =
          primary_membership["renewal_promotion_code_name__c"];

        // If they don't match and renewal code exists, show the modal
        if (
          this.renewalPromoCode &&
          this.tempFormPromoCode !== this.renewalPromoCode
        ) {
          this.showPromoCodeModal = true;
        } else {
          // Just use current promo code
          this.submitPromo(this.promotion_code);
        }
      }
    },
    switchCamera() {
      switch (this.facingMode) {
        case "environment":
          this.facingMode = "user";
          console.log("flipping to user");
          break;
        case "user":
          this.facingMode = "environment";
          console.log("flipping to environment");
          break;
      }
    },
    validateSelection(selection) {
      this.selected = selection;
      this.referring_account = selection.sfid;
    },

    getDropdownValues(keyword) {
      if (keyword === null || keyword === undefined) {
        return;
      }

      if (!keyword || keyword.length < 2) {
        return;
      }

      searchAccounts(keyword).then((response) => {
        this.referringAccountOptions = response.data;
      });
    },
    updateContactInfo() {
      console.log("attmepting to update contact information");

      // sfids default to null, so `!== undefined` never caught it and the PATCH
      // matched on sfid IS NULL.
      if (this.contact_sfid == null) {
        console.log("contact sfid is null, nothing to update");
        return;
      }

      //prepare axios data
      var contact_data = {
        sfid: this.contact_sfid,
      };

      if (this.isRenew === "true" || this.isRenew === true) {
        contact_data.firstname = this.contacts.firstname;
        contact_data.lastname = this.contacts.lastname;
        contact_data.email = this.contacts.email;
        contact_data.phone = this.contacts.phone;
        contact_data.mobilephone = this.contacts.mobilephone;
      }

      //pop out all null values
      Object.keys(contact_data).forEach(
        (key) => contact_data[key] == null && delete contact_data[key]
      );

      //send axios data
      patchContact(contact_data)
        .then((response) => {
          console.log(response);
          // if success, toast saying updated
          this.$bvToast.toast("Contact information updated successfully", {
            title: "Contact Updated",
            variant: "success",
            solid: true,
          });
        })
        .catch((error) => {
          this.$bvToast.toast(
            "Failed to update contact information/something went wrong",
            {
              title: "Update Error",
              variant: "danger",
              solid: true,
            }
          );
          console.error(error);
        });

      //send axios post to
      this.updateAccountName();
    },
    updateBoatInfo() {
      console.log("attmepting to update boat information");

      if (this.checkAORMatch() === false) {
        console.log("AORs don't match");
        this.$bvToast.toast(
          "boat update not allowed because it will result in AOR change. Please contact membership@seatow.com with boat and home port information.",
          {
            title: "AOR Mismatch",
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      console.log("aors match, able to update boat information");

      // Null sfid would PATCH on sfid IS NULL; `!== undefined` never caught it.
      if (this.boat_sfid == null) {
        console.log("boat sfid is null, nothing to update");
        return;
      }

      //prepare axios data
      var boat_data = {
        sfid: this.boat_sfid,
      };

      boat_data.name__c = this.boats.name__c;
      boat_data.year__c = this.boats.year__c;
      boat_data.length__c = this.boats.length__c;
      boat_data.boat_make__c = this.boats.boat_make__c;
      boat_data.engine_type__c = this.boats.engine_type__c;
      boat_data.color__c = this.boats.color__c;
      boat_data.home_port_type__c = this.boats.home_port_type__c;
      boat_data.home_port_city__c = this.boats.home_port_city__c;
      boat_data.home_port_state__c = this.boats.home_port_state__c;
      boat_data.home_port_country__c = this.boats.home_port_country__c;
      boat_data.marina_name__c = this.boats.marina_name__c;
      boat_data.registration_document__c = this.boats.registration_document__c;

      //pop out all null values
      Object.keys(boat_data).forEach(
        (key) => boat_data[key] == null && delete boat_data[key]
      );

      //pop out anything that starts with "home_port"
      Object.keys(boat_data).forEach((key) => {
        if (key.startsWith("home_port")) {
          delete boat_data[key];
        }
      });

      //send axios data
      patchBoat(boat_data)
        .then((response) => {
          console.log(response);
          this.$bvToast.toast("Boat information updated successfully", {
            title: "Boat Updated",
            variant: "success",
            solid: true,
          });
        })
        .catch((error) => {
          console.error(error);
          this.$bvToast.toast(
            "Failed to update boat information/something went wrong",
            {
              title: "Update Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    updateAccountName() {
      console.log("attmepting to update account name");
      //prepare axios data
      var account_data = {
        sfid: this.account_sfid,
      };

      // Null sfid would PATCH on sfid IS NULL; `!== undefined` never caught it.
      if (this.account_sfid == null) {
        console.log("account sfid is null, nothing to update");
        return;
      }

      account_data.name = this.account.name;

      //send axios data
      patchAccount(account_data)
        .then((response) => {
          console.log(response);
          // add toast saying success
          this.$bvToast.toast("Account name updated successfully", {
            title: "Account Name Updated",
            variant: "success",
            solid: true,
          });
        })
        .catch((error) => {
          console.error(error);
          this.$bvToast.toast(
            "Failed to update account name/something went wrong",
            {
              title: "Update Error",
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    updateAddressInfo(type) {
      console.log("attmepting to update address information");
      //prepare axios data
      var address_data = {
        sfid: this.account_sfid,
      };

      if (this.account_sfid == null) {
        console.log("account sfid is null, nothing to update");
        return;
      }

      if (type === "both") {
        address_data.shippingstreet = this.account.billingstreet;
        address_data.shippingcity = this.account.billingcity;
        address_data.shippingstate = this.account.billingstate;
        address_data.shippingpostalcode = this.account.billingpostalcode;
        address_data.shippingcountry = this.account.billingcountry;
        address_data.billingstreet = this.account.billingstreet;
        address_data.billingcity = this.account.billingcity;
        address_data.billingstate = this.account.billingstate;
        address_data.billingpostalcode = this.account.billingpostalcode;
        address_data.billingcountry = this.account.billingcountry;
      } else if (type === "billing") {
        address_data.billingstreet = this.account.billingstreet;
        address_data.billingcity = this.account.billingcity;
        address_data.billingstate = this.account.billingstate;
        address_data.billingpostalcode = this.account.billingpostalcode;
        address_data.billingcountry = this.account.billingcountry;
      } else if (type === "shipping") {
        address_data.shippingstreet = this.account.shippingstreet;
        address_data.shippingcity = this.account.shippingcity;
        address_data.shippingstate = this.account.shippingstate;
        address_data.shippingpostalcode = this.account.shippingpostalcode;
        address_data.shippingcountry = this.account.shippingcountry;
      }

      //pop out all null values
      Object.keys(address_data).forEach(
        (key) => address_data[key] == null && delete address_data[key]
      );

      //send axios data
      patchAccount(address_data).then((response) => {
        console.log(response);
        // add toast saying success
        this.$bvToast.toast("Address information updated successfully", {
          title: "Address Updated",
          variant: "success",
          solid: true,
        });
      });
    },
    setCachedName() {
      if (auth.user() != undefined && auth.user() != null) {
        //Set cachedName once so that it doesn't ever get overwritten
        if (this.cachedName == null || this.cachedName == undefined) {
          if (auth.user() != null || auth.user() != undefined) {
            console.log(`CACHING : ${auth.user().username}`);
            this.cachedName = auth.user().username;
          }
        }

        return auth.user().username;
      }

      return "pgillan@seatow.com";
    },
    codeArrived(event) {
      console.log(event);
      console.log("camera scanned");
      //if length is 18 and starts with "001"
      if (event.length === 18 && event.startsWith("001")) {
        console.log("account found: " + event);
        this.shouldShowCamera = false;
        this.RenewMembershipViaAccountId(event);
      } else {
        console.log("account not found: " + event);
        this.shouldShowCamera = false;
        this.$bvToast.toast(
          "The QR code scanned does not appear to be a valid Sea Tow membership.",
          {
            title: "Invalid QR code scanned",
            variant: "danger",
            autoHideDelay: 5000,
          }
        );
      }
    },
    errorCaptured(error) {
      switch (error.name) {
        case "NotAllowedError":
          this.cameraError = "Camera permission denied.";
          break;
        case "NotFoundError":
          this.cameraError = "There is no connected camera.";
          break;
        case "NotSupportedError":
          this.cameraError =
            "Seems like this page is served in non-secure context.";
          break;
        case "NotReadableError":
          this.cameraError =
            "Couldn't access your camera. Is it already in use?";
          break;
        case "OverconstrainedError":
          this.cameraError = "Constraints don't match any installed camera.";
          break;
        default:
          this.cameraError = "UNKNOWN CAMERA ERROR: " + error.message;
      }
      console.error(this.errorMessage);
    },
    showModal() {
      this.$refs["my-modal"].show();
    },
    hideModal() {
      this.clearForm();
      this.$v.$reset();
      // Force update to ensure checkboxes reflect the cleared state
      this.$nextTick(() => {
        this.$forceUpdate();
      });
      this.$refs["my-modal"].hide();
    },
    checkTrialEligibility() {
      // Validate trial eligibility based on Salesforce formula:
      // IF(AND(NOT(ISBLANK(Trial_Issued_Date__c)), NOT(ISNULL(Next_Trial_Eligible_in_Days__c))),
      //    Trial_Issued_Date__c + Next_Trial_Eligible_in_Days__c, null)

      const trialIssuedDate = this.memberships.trial_issued_date__c;
      let nextTrialEligibleDays =
        this.memberships.next_trial_eligible_in_days__c;

      console.log("=== Trial Eligibility Check ===");
      console.log("trial_issued_date__c:", trialIssuedDate);
      console.log(
        "next_trial_eligible_in_days__c (raw):",
        nextTrialEligibleDays
      );

      // If trial_issued_date__c is blank, member is eligible (no trial history)
      if (!trialIssuedDate) {
        console.log(
          "trial_issued_date__c is blank - member IS ELIGIBLE (no trial history)"
        );
        this.nextTrialEligibleDate = null;
        return true;
      }

      // If trial_issued_date__c is populated but next_trial_eligible_in_days__c is null/undefined, default to 365
      if (
        nextTrialEligibleDays == null ||
        nextTrialEligibleDays === undefined
      ) {
        console.log(
          "trial_issued_date__c is populated but next_trial_eligible_in_days__c is null - defaulting to 365 days"
        );
        nextTrialEligibleDays = 365;
      }

      console.log(
        "next_trial_eligible_in_days__c (final):",
        nextTrialEligibleDays
      );

      // Calculate next eligible date
      const issuedDate = new Date(trialIssuedDate);
      const eligibleDate = new Date(issuedDate);
      eligibleDate.setDate(
        eligibleDate.getDate() + parseInt(nextTrialEligibleDays)
      );

      console.log("Trial issued date:", issuedDate.toLocaleDateString());
      console.log("Days to add:", nextTrialEligibleDays);
      console.log("Next eligible date:", eligibleDate.toLocaleDateString());

      // Check if today is past the eligible date
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset to start of day for comparison
      eligibleDate.setHours(0, 0, 0, 0);

      console.log("Today:", today.toLocaleDateString());
      console.log("Is today >= eligible date?", today >= eligibleDate);

      if (today >= eligibleDate) {
        // Eligible - date has passed
        console.log("Result: ELIGIBLE (date has passed)");
        this.nextTrialEligibleDate = null;
        return true;
      } else {
        // Not eligible yet
        console.log(
          "Result: NOT ELIGIBLE (must wait until",
          eligibleDate.toLocaleDateString(),
          ")"
        );
        this.nextTrialEligibleDate = eligibleDate;
        return false;
      }
    },
    closeTrialEligibilityModal() {
      console.log("Closing trial eligibility modal");
      this.$refs["trial-eligibility-modal"].hide();
    },
    revertCardSelection() {
      // Revert to previous selection when modal is closed
      console.log(
        "Reverting card selection from",
        this.CardSelection,
        "to",
        this.previousCardSelection
      );

      // If previous selection was also Trial, default to Gold instead
      if (
        this.previousCardSelection &&
        this.previousCardSelection.includes("Trial")
      ) {
        console.log("Previous selection was also Trial, defaulting to Gold");
        this.CardSelection = "Gold";
        this.previousCardSelection = "Gold";
        this.updateCartPrice("Gold");
      } else {
        this.CardSelection = this.previousCardSelection;
        this.updateCartPrice(this.previousCardSelection);
      }
    },
    FillAccountName() {
      this.account.name =
        this.capitalizeFirstLetter(this.contacts.firstname) +
        " " +
        this.capitalizeFirstLetter(this.contacts.lastname);
    },
    getPostalCodeInfo(zipcode, which_address) {
      if (zipcode.length == 5) {
        let data = {
          zipcode: zipcode,
        };

        searchByZip(data).then((response) => {
          console.log(response["data"]);
          if (which_address == "billing") {
            this.account.billingcity = response["data"]["major_city"];
            this.account.billingstate = toStateCode(response["data"]["state"]);
          } else if (which_address == "shipping") {
            this.account.shippingcity = response["data"]["major_city"];
            this.account.shippingstate = toStateCode(response["data"]["state"]);
          } else if (this.shipping_same_as_billing) {
            this.account.shippingcity = response["data"]["major_city"];
            this.account.shippingstate = toStateCode(response["data"]["state"]);
            this.account.billingcity = response["data"]["major_city"];
            this.account.billingstate = toStateCode(response["data"]["state"]);
          }
        });
      }
    },
    formatDollars() {
      if (this.donation_amount != "") {
        var num = this.donation_amount;
        num = Number(num);

        var countDecimals = function (value) {
          if (Math.floor(value) === value) return 0;
          return value.toString().split(".")[1].length || 0;
        };

        var decimal = countDecimals(num);

        if (decimal < 2) {
          num = num.toFixed(2);
        }

        if (decimal > 2) {
          num = num.toFixed(decimal);
        }

        if (parseInt(num) < 1) {
          num = "." + String(num).split(".")[1];
        }

        this.donation_amount = parseFloat(num);
        this.updateCartPrice();
      }
    },
    async submitSearchForm() {
      this.clearForm();
      this.$bvToast.toast(
        `Searching using '${this.membership_number__c}' as criteria.`,
        {
          title: "Searching for member",
          variant: "info",
          autoHideDelay: 5000,
        }
      );

      // if the search term looks like a phone number (parenthesis, 10 digits, and at least a dash), strip non numeric
      if (
        this.membership_number__c.match(
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
        )
      ) {
        this.membership_number__c = this.membership_number__c.replace(
          /\D/g,
          ""
        );
      }

      let data = {
        search_term: this.membership_number__c,
        search_type: this.search_type,
      };
      searchMembership(data).then((response) => {
        this.toggleBusy();
        console.log(response["data"]);
        try {
          if (Array.isArray(response["data"])) {
            response["data"].forEach((element) => {
              element["show_details"] = false;
              // Check for active payment profile to determine ARB status
              // Use has_active_payment_profile from API if available, otherwise fall back to auto_renew__c
              if (element["has_active_payment_profile"] !== undefined) {
                element["auto_renew__c"] =
                  element["has_active_payment_profile"] === true
                    ? "Enrolled"
                    : "No ARB";
              } else {
                element["auto_renew__c"] =
                  element["auto_renew__c"] === true ||
                  element["auto_renew__c"] === "true"
                    ? "Enrolled"
                    : "No ARB";
              }
              // Ensure account_name__c is populated - try alternative field names if not present
              if (!element["account_name__c"]) {
                element["account_name__c"] =
                  element["account_name"] ||
                  element["name"] ||
                  element["accountname"] ||
                  "";
              }
            });
          } else {
            console.error("response.data is not an array:", response["data"]);
          }
        } catch (error) {
          console.error("Error processing response data:", error);
        }

        this.response_data = response["data"];
        // A new search starts trimmed, however far the last one was opened.
        this.showAllResults = false;

        if (this.response_data.length > 0) {
          this.$bvToast.toast(
            `Found ${this.response_data.length} possible ${
              this.response_data.length > 1 ? "matches" : "match"
            }.`,
            {
              title: "Matches found.",
              variant: "success",
              autoHideDelay: 5000,
            }
          );
        } else {
          this.$bvToast.toast(
            "No matches, nothing to display. Try refining your search criteria.",
            {
              title: "No matches found.",
              variant: "warning",
              autoHideDelay: 5000,
            }
          );
        }
      });
    },
    toggleCamera() {
      this.shouldShowCamera = !this.shouldShowCamera;
    },
    clearTable() {
      this.response_data = [];
      this.membership_number__c = "";
      this.showAllResults = false;
    },
    clearReferringAccount() {
      this.referring_account = null;
      this.referringAccountOptions = [{ name: "placeholder", id: 1 }];
      this.selected = { name: null, id: null };
      this.$refs["dropdown"].searchFilter = null;
    },
    // Drop every id tying the form to a loaded member. A leftover id makes the
    // server update that member instead of creating one.
    // Bind a different membership on this account into the form below. The
    // whole renewal and payment chain reads the flat state (this.memberships,
    // this.contacts, this.boats, the *_sfid fields), so re-binding here is what
    // makes the Submit button renew the selected membership.
    selectMembershipContext(index) {
      const context = this.membershipContexts[index];
      if (!context || index === this.selectedMembershipIndex) {
        return;
      }

      if (!context.contact || !context.boat) {
        this.$bvToast.toast(
          "That membership is missing its contact or boat record, so it cannot be loaded for renewal. You can still edit it in its panel.",
          {
            title: "Incomplete membership",
            variant: "warning",
            autoHideDelay: 8000,
          }
        );
        return;
      }

      // Collapse the panel that was open so the account does not end up with
      // several expanded at once, all but one of them stale.
      this.membershipContexts.forEach((item, itemIndex) => {
        item.expanded = itemIndex === index;
      });

      this.selectedMembershipIndex = index;

      this.populateRenewalForm(this.full_data, {
        primary_membership: context.membership,
        primary_contact: context.contact,
        primary_boat: context.boat,
      });

      this.trackUxcamEvent("membership_context_selected", {
        membership_sfid: context.membership.sfid,
        member_number: context.membership.membership_number__c,
        is_primary: context.isPrimary,
        memberships_on_account: this.membershipContexts.length,
      });

      this.$bvToast.toast(
        `Membership #${context.membership.membership_number__c} is now loaded in the form.`,
        {
          title: "Membership selected",
          variant: "info",
          autoHideDelay: 4000,
        }
      );
    },

    // The panel edits below patch only the records belonging to that panel, so
    // a correction to a secondary membership never disturbs the renewal that is
    // in progress in the form.
    patchFromPanel(context, label, patchFn, payload) {
      if (!payload.sfid) {
        // No sfid means Salesforce does not have this record: either it is
        // still on its way through Heroku Connect or Salesforce refused it.
        // Patching it would write to Postgres, report success, and never
        // reach Salesforce - which is exactly the failure the panels are
        // meant to make visible rather than repeat.
        this.$bvToast.toast(
          `This ${label.toLowerCase()} is not in Salesforce yet, so it cannot be updated here. If it stays that way, Salesforce rejected it and it needs fixing there.`,
          {
            title: "Not in Salesforce",
            variant: "warning",
            autoHideDelay: 9000,
          }
        );
        return;
      }

      context.saving = true;
      patchFn(payload)
        .then(() => {
          this.$bvToast.toast(`${label} updated successfully`, {
            title: "Saved",
            variant: "success",
            autoHideDelay: 5000,
          });
        })
        .catch((error) => {
          devError(`Panel ${label} update failed:`, error);
          this.$bvToast.toast(
            `We couldn't update the ${label.toLowerCase()}. Please try again.`,
            {
              title: "Update failed",
              variant: "danger",
              autoHideDelay: 8000,
            }
          );
        })
        .finally(() => {
          context.saving = false;
        });
    },

    // The panel says which record it edited. Any contact on the account and any
    // boat on the membership can be edited from a panel, so the handler can no
    // longer assume "the one this membership points at".
    //
    // The main form's own models are the exception: they hold the field values
    // but not the sfid, which the form tracks separately, so identity comes
    // from the form when the object handed back IS the form's model.
    // Called once the loaded contact has been copied into the form.
    rememberLoadedPhones() {
      this.loadedPhones = {
        phone: normalizePhone(this.contacts.phone),
        mobilephone: normalizePhone(this.contacts.mobilephone),
      };
    },

    // A number is acceptable if the dealer did not put it there. Anything they
    // typed has to be dialable; anything the member already had is left alone,
    // because refusing it here refuses the payment too.
    isPhoneAcceptable(field, value) {
      const digits = normalizePhone(value);
      if (digits && digits === this.loadedPhones[field]) {
        return true;
      }
      return isValidNanpPhone(value);
    },

    // Delegates to the shared formatter so the panel's auto-renew summary and
    // this form cannot disagree about how a date reads.
    formatCalendarDate(raw) {
      return formatCalendarDate(raw);
    },

    resolveSfid(record, formModel, formSfid) {
      if (record === formModel) return formSfid;
      return record && record.sfid;
    },

    updateContextContact(context, edited) {
      const contact = edited || context.contact || {};
      const sfid = this.resolveSfid(contact, this.contacts, this.contact_sfid);
      this.patchFromPanel(context, "Contact", patchContact, {
        sfid,
        firstname: contact.firstname,
        lastname: contact.lastname,
        email: contact.email,
        // Store the ten digits, the way the add-contact form and the renewal
        // form both do. Sending what was typed wrote "(631) 555-2342" - or
        // worse - into a column the rest of the app reads as ten digits.
        phone: normalizePhone(contact.phone) || contact.phone,
        mobilephone: normalizePhone(contact.mobilephone) || contact.mobilephone,
      });
    },

    updateContextBoat(context, edited) {
      const boat = edited || context.boat || {};
      const sfid = this.resolveSfid(boat, this.boats, this.boat_sfid);
      this.patchFromPanel(context, "Boat", patchBoat, {
        sfid,
        name__c: boat.name__c,
        boat_make__c: boat.boat_make__c,
        year__c: boat.year__c,
        length__c: boat.length__c,
        registration_document__c: boat.registration_document__c,
        // primary_boat__c is deliberately not sent. Which boat is primary can
        // move the area of responsibility, and money moves with it, so that
        // stays in Salesforce - but the status of a boat is just its status.
        boat_status__c: boat.boat_status__c,
      });
    },

    // Repoint the membership at a different contact on the account. This is a
    // membership field, but unlike expiration or trailer care it carries no
    // pricing, so it stays editable here.
    setContextContact(context, contactSfid) {
      if (
        !contactSfid ||
        contactSfid === context.membership.membership_contact__c
      ) {
        return;
      }

      const previous = context.membership.membership_contact__c;
      context.saving = true;
      patchMembership({
        sfid: context.membership.sfid,
        membership_contact__c: contactSfid,
      })
        .then(() => {
          context.membership.membership_contact__c = contactSfid;
          context.contact =
            this.accountContacts.find((item) => item.sfid === contactSfid) ||
            context.contact;
          if (
            context === this.membershipContexts[this.selectedMembershipIndex]
          ) {
            this.contact_sfid = contactSfid;
          }
          this.$bvToast.toast("Membership contact updated successfully", {
            title: "Saved",
            variant: "success",
            autoHideDelay: 5000,
          });
        })
        .catch((error) => {
          devError("Membership contact repoint failed:", error);
          context.membership.membership_contact__c = previous;
          this.$bvToast.toast(
            "We couldn't change the contact on this membership. Please try again.",
            {
              title: "Update failed",
              variant: "danger",
              autoHideDelay: 8000,
            }
          );
        })
        .finally(() => {
          context.saving = false;
        });
    },

    addContextContact(context, fields) {
      if (!this.account_sfid) {
        this.$bvToast.toast(
          "No account is loaded, so a contact cannot be added.",
          {
            title: "Nothing to add",
            variant: "warning",
            autoHideDelay: 5000,
          }
        );
        return;
      }

      context.saving = true;
      // `contact` has no account__c column - the account link is `accountid`.
      // Sending one made the insert fail to compile, which is what surfaced as
      // "We couldn't add that contact".
      //
      // The record type is sent explicitly rather than relying on the API's
      // default, so the value the dealer picked is validated against the record
      // type the row is actually written under.
      postContact({
        ...fields,
        accountid: this.account_sfid,
        recordtypeid: this.contactRecordTypeId,
      })
        .then((response) => {
          // The contact belongs to the account, so every panel's picker gains it.
          this.accountContacts.push({
            ...fields,
            sfid: response?.data?.sfid || null,
          });
          this.$bvToast.toast(
            "Contact added. Select it above to attach it to this membership.",
            {
              title: "Contact added",
              variant: "success",
              autoHideDelay: 6000,
            }
          );
        })
        .catch((error) => {
          devError("Add contact failed:", error);
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Please try again.";
          this.$bvToast.toast(`We couldn't add that contact. ${message}`, {
            title: "Add failed",
            variant: "danger",
            autoHideDelay: 8000,
          });
        })
        .finally(() => {
          context.saving = false;
        });
    },

    // A secondary boat inherits the membership's home port and AOR, so adding
    // one cannot move the area of responsibility. Making a boat primary is
    // deliberately not offered here - that decision moves money between
    // franchisees, so it belongs in Salesforce.
    addContextBoat(context, fields) {
      const primary = context.boat;
      if (!primary) {
        this.$bvToast.toast(
          "This membership has no boat to copy a home port from, so a boat cannot be added here.",
          {
            title: "Nothing to inherit",
            variant: "warning",
            autoHideDelay: 8000,
          }
        );
        return;
      }

      context.saving = true;
      const payload = {
        ...fields,
        primary_boat__c: false,
        boat_status__c: "Active",
        account__c: this.account_sfid,
        contact__c: context.membership.membership_contact__c,
        related_membership__c: context.membership.sfid,
        heroku_external_id__c: this.guid(),
        aor__c: primary.aor__c,
        home_port_type__c: primary.home_port_type__c,
        home_port_city__c: primary.home_port_city__c,
        home_port_state__c: primary.home_port_state__c,
        home_port_country__c: primary.home_port_country__c,
        marina_name__c: primary.marina_name__c,
      };

      postBoat(payload)
        .then((response) => {
          context.boats.push({
            ...payload,
            sfid: response?.data?.sfid || null,
          });
          this.$bvToast.toast(
            "Boat added as a secondary vessel. To make it the primary boat, open the membership in Salesforce.",
            {
              title: "Boat added",
              variant: "success",
              autoHideDelay: 8000,
            }
          );
        })
        .catch((error) => {
          devError("Add boat failed:", error);
          this.$bvToast.toast("We couldn't add that boat. Please try again.", {
            title: "Add failed",
            variant: "danger",
            autoHideDelay: 8000,
          });
        })
        .finally(() => {
          context.saving = false;
        });
    },

    clearRecordIdentity() {
      this.contact_sfid = null;
      this.boat_sfid = null;
      this.membership_sfid = null;
      this.account_sfid = null;
      this.arbs_sfid = null;
      this.isRenew = false;

      Object.keys(this.routes).forEach((name) => {
        this.routes[name] = null;
      });
      Object.keys(this.sfid).forEach((name) => {
        this.sfid[name] = null;
      });
      Object.keys(this.arbs).forEach((name) => {
        this.arbs[name] = null;
      });

      this.passed_in_acc_data = [];
      this.full_data = [];
      this.membershipContexts = [];
      this.selectedMembershipIndex = 0;
      this.accountContacts = [];
      // Nothing is loaded any more, so nothing is grandfathered.
      this.loadedPhones = { phone: null, mobilephone: null };
      this.showAllMemberships = false;
    },
    clearForm() {
      this.clearRecordIdentity();

      // Call clearStripeSession on StripePaymentForm child component via $refs
      if (
        this.$refs.stripePaymentForm &&
        typeof this.$refs.stripePaymentForm.clearStripeSession === "function"
      ) {
        this.$refs.stripePaymentForm.clearStripeSession();
      }
      // Reset Stripe payment initialization flag
      this.stripePaymentInitialized = false;

      // Clear all Stripe payment data from previous transactions
      this.clearStripePaymentData();

      if (!this.do_not_clear_cc || !this.do_not_clear == "true") {
        this.do_not_clear_cc = false;
        this.additional_cc_list = "";
      }

      //reset state of <Dropdown> for referring account
      this.referring_account = null;
      this.referringAccountOptions = [];
      this.selected = { name: null, id: null };
      this.$refs["dropdown"].searchFilter = null;

      var contact_parsed_obj = JSON.parse(JSON.stringify(this.contacts));
      var contact_keynames = Object.keys(contact_parsed_obj);
      var account_parsed_obj = JSON.parse(JSON.stringify(this.account));
      var account_keynames = Object.keys(account_parsed_obj);
      var boats_parsed_obj = JSON.parse(JSON.stringify(this.boats));
      var boats_keynames = Object.keys(boats_parsed_obj);
      var memberships_parsed_obj = JSON.parse(JSON.stringify(this.memberships));
      var memberships_keynames = Object.keys(memberships_parsed_obj);

      contact_keynames.forEach((name) => {
        this.contacts[name] = null;
      });

      account_keynames.forEach((name) => {
        this.account[name] = null;
      });

      boats_keynames.forEach((name) => {
        this.boats[name] = null;
      });

      memberships_keynames.forEach((name) => {
        this.memberships[name] = null;
      });

      this.memno = null;
      this.isHomeportFlorida = false;

      // Set flag to skip trial validation when clearing form
      this.isLoadingMembership = true;
      this.CardSelection = "Gold";
      this.TrailerSelection = "None";
      this.isRenew = false;
      this.$nextTick(() => {
        this.isLoadingMembership = false;
      });
      this.account.billingstate = null;
      this.boats.boat_transfer_type__c = null;
      this.autorenew_status = false;
      this.arb_value = false;
      this.arb_enroll_only = false;
      this.selected_trial_time_product = null;
      this.funds_collected_locally = false;
      console.log("Cleared arb_value to:", this.arb_value);
      console.log(
        "Cleared funds_collected_locally to:",
        this.funds_collected_locally
      );
      this.disableSubmit = false;
      this.membership_status__c = null;
      this.membership_expiration_date__c = null;
      this.old_aor = null;

      if (this.do_not_clear_promo === "false") {
        this.do_not_clear_promo = "false";
        this.promotion_title = null;
        this.promotion_value_percentage_discount = null;
        this.promotion_sfid = null;
        this.promotion_valid_on_business_type__c = null;
        this.promotion_valid_on_membership__c = null;
        this.promotion_state_value = null;
        this.promotion_valid = false;
        this.promotion_price = 0;
        this.related_product_details = null;
        this.promotion_type__c = null;
        this.title__c = null;
        this.promotion_value_in_dollars = null;
        this.promotion_value_in_days = null;
        this.promotion_code = null;
        this.promotion_details__c = null;
        this.promotion_desc = null;
      }

      this.price_total = 249.0;
      this.donation_amount = 0;
      this.trailering_price = 0;
      this.cc_declined = false;
      this.refTransId = null;
      this.account.shippingcountry = "United States";
      this.account.billingcountry = "United States";
      this.boats.home_port_country__c = "United States";
      this.aor_display_field = "999 (International)";

      // Activation code fields
      Object.keys(this.activation_codes).forEach((key) => {
        console.log("Setting " + key + " to null");
        this.activation_codes[key] = null;
      });
      //Set the ActivationCodeInput values to default
      this.$refs.activationCodeInput.reset();

      this.$bvToast.toast("Data has been cleared from the form.", {
        title: "Data cleared",
        variant: "info",
        autoHideDelay: 5000,
      });
    },

    toggleBusy() {
      this.isBusy = !this.isBusy;
    },
    assignAOR() {
      let data = {
        street: this.boats.marina_name__c,
        city: this.boats.home_port_city__c,
        state: this.boats.home_port_state__c,
        // The boat's own country, not a hardcoded one. 936 synced boats are
        // homeported outside the United States (Canada 690, Other 150, Bahamas
        // 71, Mexico 14, Panama 7, British Virgin Islands 4), and submitFormMixin
        // re-runs this on every submission - so each of those renewals was
        // asking Salesforce which US area of responsibility covers a marina
        // that is not in the US. The Apex endpoint takes a country and has an
        // international answer (AOR 999); it was never being given the chance
        // to use it.
        country: this.boats.home_port_country__c || "United States",
      };

      console.log("sending data...");
      console.log(data);

      // The boat lets the API use its stored location when the home port is unchanged.
      getAor(data, this.boat_sfid).then((response) => {
        console.log("first response: ");
        console.log(response);

        let data = {
          name: response["data"]["aor_name"],
        };

        getAorFranchiseeLink(data).then((response) => {
          console.log("2nd response = " + response);
          console.log(response);
          console.log("inside of 2nd then in assignAOR");
          console.log("new sfid = " + response["data"]["sfid"]);

          if (this.isRenew) {
            if (this.old_aor != response["data"]["sfid"]) {
              this.boats.boat_transfer_type__c =
                "Change HP AOR mid-mbrshp/on renew - Partial";
            } else {
              this.boats.boat_transfer_type__c = null;
            }
          }

          this.boats.aor__c = response["data"]["sfid"];
          this.aor_display_field = `${response["data"]["name"]} (${response["data"]["sfid"]})`;
        });
      });
    },
  },
};
</script>

<style scoped>
.show-more {
  padding-left: 0;
}

/* A one-line message should read as a message, not as a banner spanning the
   whole form. Wide enough for the longest of them, and it still wraps. */
.inline-notice {
  max-width: 34rem;
}

/* The card runs the full width like every other card on the page; it is the
   CONTENT that needed constraining. Same 600px centred column as
   .order-summary directly above it, so the two read as one stack rather than
   one full-bleed panel followed by a narrow orphan. The Submit button fills
   that column, matching the Total Price row it sits under. */
.verify-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem 0;
}

.verify-heading {
  margin-bottom: 0.75rem;
}

/* Memberships section. Without these the title and its hint render as one
   run-together line, since both are inline elements. */
.memberships-heading {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-default);
}

.memberships-heading small {
  font-size: 0.85rem;
}

.update-button {
  width: 100%;
  display: block;
}

/* Responsive container padding */
.responsive-container {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

@media (min-width: 768px) {
  .responsive-container {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
}

/* Mobile-friendly radio buttons with larger touch targets */
.mobile-friendly-radio >>> .custom-radio {
  margin-bottom: 0.75rem;
  min-height: 44px;
}

.mobile-friendly-radio >>> .custom-control-label {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.5rem;
  cursor: pointer;
  line-height: 1.5;
  min-height: 44px;
}

.mobile-friendly-radio >>> .custom-control-label::before,
.mobile-friendly-radio >>> .custom-control-label::after {
  width: 1.25rem;
  height: 1.25rem;
  top: 0.5rem;
}

@media (max-width: 767px) {
  .mobile-friendly-radio >>> .custom-radio {
    margin-bottom: 1rem;
    min-height: 48px;
  }

  .mobile-friendly-radio >>> .custom-control-label {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    padding-left: 0.75rem;
    font-size: 1.05rem;
    min-height: 48px;
  }

  .mobile-friendly-radio >>> .custom-control-label::before,
  .mobile-friendly-radio >>> .custom-control-label::after {
    width: 1.5rem;
    height: 1.5rem;
    top: 0.75rem;
  }
}

/* QR Code Scanner Styling */
.qr-reader-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 767px) {
  .qr-reader-container {
    max-width: 100%;
  }
}

/* Order Summary Receipt-Style */
.order-summary {
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem 0;
}

.summary-section {
  margin-bottom: 1rem;
}

.section-title {
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
  letter-spacing: 0.5px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  gap: 1rem;
}

.item-label {
  display: flex;
  align-items: center;
  flex: 1;
  font-size: 1rem;
}

.item-value {
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
  text-align: right;
}

.item-addition {
  color: #28a745;
}

[data-theme="dark"] .item-addition {
  color: #4ade80;
}

.item-discount {
  color: #dc3545;
}

[data-theme="dark"] .item-discount {
  color: #f87171;
}

.item-description {
  font-size: 0.875rem;
  color: var(--text-muted);
  padding: 0 0 0.5rem 2.25rem;
  font-style: italic;
}

.section-divider {
  border-top: 1px solid var(--border-default);
  margin: 1rem 0 1.25rem 0;
}

.section-divider-bold {
  border-top: 2px solid var(--text-muted);
  margin: 1.5rem 0 1.25rem 0;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: var(--bg-hover);
  border-radius: 6px;
  margin-top: 0.5rem;
}

.total-label {
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.total-value {
  font-weight: 700;
  font-size: 1.5rem;
  color: #007bff;
}

@media (max-width: 767px) {
  .order-summary {
    padding: 0.5rem 0;
  }

  .summary-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.5rem 0;
  }

  .item-label {
    font-size: 0.95rem;
  }

  .item-value {
    font-size: 1.1rem;
    align-self: flex-end;
    width: 100%;
  }

  .item-description {
    padding-left: 0;
  }

  .total-label {
    font-size: 1.1rem;
  }

  .total-value {
    font-size: 1.35rem;
  }
}

/* Badge styling in summary */
.order-summary .badge {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  font-weight: 600;
}

/* Total Breakdown for Auto-Renew */
.summary-total-breakdown {
  padding: 1rem;
  background-color: var(--bg-hover);
  border-radius: 6px;
  margin-top: 0.5rem;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.breakdown-item:not(:last-child) {
  border-bottom: 1px solid var(--border-default);
}

.breakdown-item.recurring {
  padding-top: 1rem;
}

.breakdown-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1.1rem;
}

.breakdown-value {
  font-weight: 700;
  font-size: 1.3rem;
  color: #007bff;
  white-space: nowrap;
}

.breakdown-item.recurring .breakdown-label {
  color: #28a745;
}

[data-theme="dark"] .breakdown-item.recurring .breakdown-label {
  color: #4ade80;
}

.breakdown-item.recurring .breakdown-value {
  color: #28a745;
  font-size: 1.2rem;
}

[data-theme="dark"] .breakdown-item.recurring .breakdown-value {
  color: #4ade80;
}

@media (max-width: 767px) {
  .breakdown-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .breakdown-value {
    align-self: flex-end;
    width: 100%;
    text-align: right;
  }

  .breakdown-label {
    font-size: 1rem;
  }

  .breakdown-value {
    font-size: 1.2rem;
  }

  .breakdown-item.recurring .breakdown-value {
    font-size: 1.1rem;
  }
}
</style>
